import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import "../../../../App.css";
import Navbar from "../../../../components/ui/Navbar";
import Table from "../../../../components/ui/Table";
import Modal from "../../../../components/ui/Modal";
import Loader from "../../../../components/ui/Loader";
import ModalTableInput from "../../../../components/ui/ModalTableInput";
import { AuthContext } from "../../../../context/AuthContext";
import {
  swalError,
  swalSuccess,
  swalConfirm,
  swalWarning,
} from "../../../../utils/swalAlert";
import { fetchWithAuth } from "../../../../utils/api";
import Select from "react-select";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { DEFAULT_AI_MODELS, DEFAULT_AI_PROVIDERS, getGeminiModelConfig } from "../../../../config/aiModelConfig";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { userCountry } from "../../../../utils/getUserCountry";
import {
  defaultCOPOConfig,
  validateCOWeightage,
  validateDirectIndirectCOWeightage,
  validateDirectIndirectPOWeightage,
} from "../../../../utils/copoConfig";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

const defaultEditFormData = {
  id: "",
  name: "",
  tagline: "",
  address: "",
  code: "",
  domain: "",
  category: "",
  standardizedTestTemplateIds: [],
  curriculum: "",
  phoneNumber: "",
  formattedPhoneNumber: "",
  country: "",
  websiteURL: "",
  dataRegion: "",
  webhookEnabled: false,
  isImageCreationAllowed: false,
  isExternalBatchProcessing: false,
  isManualGradingAllowed: false,
  isOnlineExamAllowed: false,
  isAdvancedOnlineExamAllowed: false,
  hasSubscribed: false,
  allowStudentClassJoin: false,
  autoGenerateStudentPassword: false,
  allowHomeworkGrading: false,
  webhookURL: "",
  logo: null,
  logoURL: "",
  logoChanged: false,
  combinedGradingBlockSize: "",
  questionBlockSize: "",
  whatsAppAccessToken: "",
  whatsAppPhoneNumberId: "",
  whatsAppNotificationLimit: "",
  isPageRangeRulesEnabled: false,
  pageRangeCreditUsageRules: [
    { minPageCount: 1, maxPageCount: "", creditUsage: "" },
  ],
  isCOPOEnabled: false,
  isQRPageCountDetectionEnabled: false,
  isReportCardEnabled: false,
  isCreditReportEnabled: false,
  // CO-PO Config fields
  coAttainmentMethod: defaultCOPOConfig.coAttainmentMethod,
  level1MinScorePercentage:
    defaultCOPOConfig.scoreBasedAttainment.level1MinScorePercentage,
  level2MinScorePercentage:
    defaultCOPOConfig.scoreBasedAttainment.level2MinScorePercentage,
  level3MinScorePercentage:
    defaultCOPOConfig.scoreBasedAttainment.level3MinScorePercentage,
  scoreAveragingBasis: defaultCOPOConfig.scoreAveragingBasis,
  minScorePercentage:
    defaultCOPOConfig.thresholdBasedAttainment.minScorePercentage,
  level1MinStudentPercentage:
    defaultCOPOConfig.thresholdBasedAttainment.level1MinStudentPercentage,
  level2MinStudentPercentage:
    defaultCOPOConfig.thresholdBasedAttainment.level2MinStudentPercentage,
  level3MinStudentPercentage:
    defaultCOPOConfig.thresholdBasedAttainment.level3MinStudentPercentage,
  internalCOWeightage: defaultCOPOConfig.internalCOWeightage,
  externalCOWeightage: defaultCOPOConfig.externalCOWeightage,
  directCOWeightage: defaultCOPOConfig.directCOWeightage,
  indirectCOWeightage: defaultCOPOConfig.indirectCOWeightage,
  directPOWeightage: defaultCOPOConfig.directPOWeightage,
  indirectPOWeightage: defaultCOPOConfig.indirectPOWeightage,
  feedbackMode: "APP_FEEDBACK_ONLY",
  slaMaxProcessingHours: "",
  annotationPrintingOffsetConfig: [],
  aiProviderConfig: [],
};

const groupConfigsByProvider = (configs = []) => {
  const gemini = [];
  const openai = [];
  const other = [];

  for (const c of configs) {
    const provider = (c.provider || "").toLowerCase();
    if (provider === "gemini") {
      gemini.push(c);
    } else if (provider === "openai") {
      openai.push(c);
    } else {
      other.push(c);
    }
  }

  return { gemini, openai, other };
};

const checkIsEditChanged = (current, initial) => {
  if (!current || !initial) return false;

  // Basic string/value fields
  const stringFields = [
    "name",
    "tagline",
    "address",
    "code",
    "domain",
    "category",
    "curriculum",
    "phoneNumber",
    "websiteURL",
    "whatsAppAccessToken",
    "whatsAppPhoneNumberId",
    "dataRegion",
    "webhookURL",
    "feedbackMode",
    "coAttainmentMethod",
    "scoreAveragingBasis",
    "reportCardTemplate",
  ];

  for (const field of stringFields) {
    if ((current[field] ?? "") !== (initial[field] ?? "")) {
      return true;
    }
  }

  // Boolean fields
  const booleanFields = [
    "webhookEnabled",
    "isImageCreationAllowed",
    "isExternalBatchProcessing",
    "isManualGradingAllowed",
    "isOnlineExamAllowed",
    "isAdvancedOnlineExamAllowed",
    "hasSubscribed",
    "allowStudentClassJoin",
    "autoGenerateStudentPassword",
    "allowHomeworkGrading",
    "isPageRangeRulesEnabled",
    "isCOPOEnabled",
    "isQRPageCountDetectionEnabled",
    "isReportCardEnabled",
    "isCreditReportEnabled",
  ];

  for (const field of booleanFields) {
    if (Boolean(current[field]) !== Boolean(initial[field])) {
      return true;
    }
  }

  // Number / String numeric fields
  const numericFields = [
    "whatsAppNotificationLimit",
    "slaMaxProcessingHours",
    "minScorePercentage",
    "level1MinStudentPercentage",
    "level2MinStudentPercentage",
    "level3MinStudentPercentage",
    "level1MinScorePercentage",
    "level2MinScorePercentage",
    "level3MinScorePercentage",
    "internalCOWeightage",
    "externalCOWeightage",
    "directCOWeightage",
    "indirectCOWeightage",
    "directPOWeightage",
    "indirectPOWeightage",
  ];

  for (const field of numericFields) {
    const curVal = current[field] != null ? String(current[field]).trim() : "";
    const initVal = initial[field] != null ? String(initial[field]).trim() : "";
    if (curVal !== initVal) {
      return true;
    }
  }

  // Standardized Test Template IDs (array comparison preserving selection order)
  const curTemplates = current.standardizedTestTemplateIds || [];
  const initTemplates = initial.standardizedTestTemplateIds || [];
  if (JSON.stringify(curTemplates) !== JSON.stringify(initTemplates)) {
    return true;
  }

  // Annotation Printing Offset Config array comparison
  const curOffsetConfig = (current.annotationPrintingOffsetConfig || []).map((c) => ({
    ...(c._id ? { _id: String(c._id) } : {}),
    name: String(c.name ?? "").trim(),
    topOffset: String(c.topOffset ?? "").trim(),
    rightOffset: String(c.rightOffset ?? "").trim(),
  }));
  const initOffsetConfig = (initial.annotationPrintingOffsetConfig || []).map((c) => ({
    ...(c._id ? { _id: String(c._id) } : {}),
    name: String(c.name ?? "").trim(),
    topOffset: String(c.topOffset ?? "").trim(),
    rightOffset: String(c.rightOffset ?? "").trim(),
  }));
  if (JSON.stringify(curOffsetConfig) !== JSON.stringify(initOffsetConfig)) {
    return true;
  }

  // AI Provider Config array comparison (feature and aiModelConfigId)
  const curAIConfig = (current.aiProviderConfig || []).map((c) => ({
    feature: String(c.feature ?? "").trim(),
    aiModelConfigId: String(c.aiModelConfigId?._id || c.aiModelConfigId || c.aiModelConfig?._id || c.aiModelConfig || "").trim(),
  }));
  const initAIConfig = (initial.aiProviderConfig || []).map((c) => ({
    feature: String(c.feature ?? "").trim(),
    aiModelConfigId: String(c.aiModelConfigId?._id || c.aiModelConfigId || c.aiModelConfig?._id || c.aiModelConfig || "").trim(),
  }));
  if (JSON.stringify(curAIConfig) !== JSON.stringify(initAIConfig)) {
    return true;
  }

  // Page range credit usage rules
  if (current.isPageRangeRulesEnabled || initial.isPageRangeRulesEnabled) {
    const curRules = (current.pageRangeCreditUsageRules || []).map((r) => ({
      minPageCount: String(r.minPageCount ?? ""),
      maxPageCount: String(r.maxPageCount ?? ""),
      creditUsage: String(r.creditUsage ?? ""),
    }));
    const initRules = (initial.pageRangeCreditUsageRules || []).map((r) => ({
      minPageCount: String(r.minPageCount ?? ""),
      maxPageCount: String(r.maxPageCount ?? ""),
      creditUsage: String(r.creditUsage ?? ""),
    }));
    if (JSON.stringify(curRules) !== JSON.stringify(initRules)) {
      return true;
    }
  }

  // Logo changes
  if (current.logo !== null || current.logoChanged === true) {
    return true;
  }

  return false;
};

export default function ManageInstitutes() {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    try {
      const saved = localStorage.getItem("table_page_size");
      if (saved) {
        const parsed = parseInt(saved, 10);
        if ([10, 25, 50, 75, 100].includes(parsed)) {
          return parsed;
        }
      }
    } catch (err) {
      console.error("Error reading table_page_size in ManageInstitutes:", err);
    }
    return 25;
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [expiredInstitutesCount, setExpiredInstitutesCount] = useState(0);
  const [error, setError] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreateAIConfigModalOpen, setIsCreateAIConfigModalOpen] =
    useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [aiProvider, setAiProvider] = useState("gemini");
  const [aiModels, setAIModels] = useState(DEFAULT_AI_MODELS);
  const [aiModel, setAiModel] = useState(null);
  const [aiModelUseTypes, setAIModelUseTypes] = useState([]);
  const [aiModelUseType, setAiModelUseType] = useState(null);
  const [dataRegionOptions, setDataRegionOptions] = useState([]);
  const [originalPageRangeRules, setOriginalPageRangeRules] = useState([]);
  const [usageMap, setUsageMap] = useState({});
  const [filterType, setFilterType] = useState("subscribed");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [standardizedTemplatesOptions, setStandardizedTemplatesOptions] = useState([]);
  const [curriculumOptions, setCurriculumOptions] = useState([]);
  const [reportCardTemplateOptions, setReportCardTemplateOptions] = useState([]);
  const [coAttainmentMethodOptions, setCoAttainmentMethodOptions] = useState(
    [],
  );
  const [scoreAveragingBasisOptions, setScoreAveragingBasisOptions] = useState(
    [],
  );
  const [feedbackModeOptions] = useState([
    { label: "ANNOTATION ONLY", value: "ANNOTATION_ONLY" },
    { label: "APP FEEDBACK ONLY", value: "APP_FEEDBACK_ONLY" },
    { label: "ANNOTATION AND APP FEEDBACK", value: "ANNOTATION_AND_APP_FEEDBACK" },
  ]);

  // Manage Credit Modal States
  const [isCreditModalOpen, setIsCreditModalOpen] = useState(false);
  const [selectedCreditInstitute, setSelectedCreditInstitute] = useState(null);
  const [creditMode, setCreditMode] = useState("auto"); // "auto" | "manual"
  const [creditFormData, setCreditFormData] = useState({
    gradingCreditExpiryDate: "",
    gradingAvailable: 0,
    gradingPastPurchased: 0,
    gradingLastPurchasedDate: "",
    gradingLastPurchasedCredits: 0,
    gradingRenewCredits: "",
    gradingRenewDate: "",
    qbAvailable: 0,
    qbPastPurchased: 0,
    qbLastPurchasedDate: "",
    qbLastPurchasedCredits: 0,
    qbRenewCredits: "",
    qbRenewDate: "",
  });
  const [isCreditSubmitting, setIsCreditSubmitting] = useState(false);
  const [creditFormError, setCreditFormError] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    tagline: "",
    address: "",
    code: "",
    domain: "",
    category: "",
    standardizedTestTemplateIds: [],
    curriculum: "",
    phoneNumber: "",
    formattedPhoneNumber: "",
    country: "",
    websiteURL: "",
    inputCostPerMillionTokens: 0.0,
    outputCostPerMillionTokens: 0.0,
    thinkingCostPerMillionTokens: 0.0,
    temperature: "",
    topK: "",
    topP: "",
    frequencyPenalty: "",
    presencePenalty: "",
    webhookEnabled: false,
    isImageCreationAllowed: false,
    isExternalBatchProcessing: false,
    isManualGradingAllowed: false,
    isOnlineExamAllowed: false,
    isAdvancedOnlineExamAllowed: false,
    hasSubscribed: false,
    allowStudentClassJoin: false,
    autoGenerateStudentPassword: false,
    allowHomeworkGrading: false,
    webhookURL: "",
    combinedGradingBlockSize: "",
    questionBlockSize: "",
    whatsAppAccessToken: "",
    whatsAppPhoneNumberId: "",
    whatsAppNotificationLimit: "",
    isPageRangeRulesEnabled: false,
    pageRangeCreditUsageRules: [
      { minPageCount: 1, maxPageCount: "", creditUsage: "" },
    ],
    isCOPOEnabled: false,
    isQRPageCountDetectionEnabled: false,
    isReportCardEnabled: false,
    isCreditReportEnabled: false,
    // CO-PO Config fields
    coAttainmentMethod: defaultCOPOConfig.coAttainmentMethod,
    // SCORE_BASED_ATTAINMENT criteria
    level1MinScorePercentage:
      defaultCOPOConfig.scoreBasedAttainment.level1MinScorePercentage,
    level2MinScorePercentage:
      defaultCOPOConfig.scoreBasedAttainment.level2MinScorePercentage,
    level3MinScorePercentage:
      defaultCOPOConfig.scoreBasedAttainment.level3MinScorePercentage,
    scoreAveragingBasis: defaultCOPOConfig.scoreAveragingBasis,
    // THRESHOLD_BASED_ATTAINMENT criteria
    minScorePercentage:
      defaultCOPOConfig.thresholdBasedAttainment.minScorePercentage,
    level1MinStudentPercentage:
      defaultCOPOConfig.thresholdBasedAttainment.level1MinStudentPercentage,
    level2MinStudentPercentage:
      defaultCOPOConfig.thresholdBasedAttainment.level2MinStudentPercentage,
    level3MinStudentPercentage:
      defaultCOPOConfig.thresholdBasedAttainment.level3MinStudentPercentage,
    // Weightages
    internalCOWeightage: defaultCOPOConfig.internalCOWeightage,
    externalCOWeightage: defaultCOPOConfig.externalCOWeightage,
    directCOWeightage: defaultCOPOConfig.directCOWeightage,
    indirectCOWeightage: defaultCOPOConfig.indirectCOWeightage,
    directPOWeightage: defaultCOPOConfig.directPOWeightage,
    indirectPOWeightage: defaultCOPOConfig.indirectPOWeightage,
    feedbackMode: "APP_FEEDBACK_ONLY",
    slaMaxProcessingHours: "",
    annotationPrintingOffsetConfig: [],
    aiProviderConfig: [],
  });
  const [editFormData, setEditFormData] = useState(defaultEditFormData);
  const [initialEditFormData, setInitialEditFormData] = useState(null);
  const [formError, setFormError] = useState("");
  const isEditChanged = useMemo(() => {
    if (!isEditModalOpen || !initialEditFormData) return false;
    return checkIsEditChanged(editFormData, initialEditFormData);
  }, [editFormData, initialEditFormData, isEditModalOpen]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const { isAuthenticated, primaryRole } = useContext(AuthContext);

  // Helper to check if institute's credit has expired
  const isCreditExpired = useCallback((institute) => {
    if (!institute?.gradingCreditExpiryDate) return false;
    const expiryDate = new Date(institute.gradingCreditExpiryDate);
    return !isNaN(expiryDate.getTime()) && expiryDate < new Date();
  }, []);

  // Bulk Delete States
  const [selectedInstituteIds, setSelectedInstituteIds] = useState(new Set());
  const [selectedInstitutesMap, setSelectedInstitutesMap] = useState(new Map());
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [bulkDeleteConfirmationText, setBulkDeleteConfirmationText] = useState("");
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState({
    current: 0,
    total: 0,
    currentName: "",
    statusMap: {},
    errorMap: {},
  });

  const selectedInstitutesList = useMemo(() => {
    return Array.from(selectedInstitutesMap.values());
  }, [selectedInstitutesMap]);

  const handleRowSelect = (e, id) => {
    const inst = tableData.find((item) => item._id === id) || selectedInstitutesMap.get(id);
    if (inst && !isCreditExpired(inst)) {
      swalWarning("Only expired institutes can be selected.");
      return;
    }
    setSelectedInstituteIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    setSelectedInstitutesMap((prev) => {
      const next = new Map(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (inst) {
        next.set(id, inst);
      }
      return next;
    });
  };

  const handleSelectAll = (visibleItemIds, areAllVisibleSelected) => {
    setSelectedInstituteIds((prev) => {
      const next = new Set(prev);
      if (areAllVisibleSelected) {
        visibleItemIds.forEach((id) => next.delete(id));
      } else {
        visibleItemIds.forEach((id) => {
          const inst = tableData.find((item) => item._id === id);
          if (inst && isCreditExpired(inst)) {
            next.add(id);
          }
        });
      }
      return next;
    });
    setSelectedInstitutesMap((prev) => {
      const next = new Map(prev);
      if (areAllVisibleSelected) {
        visibleItemIds.forEach((id) => next.delete(id));
      } else {
        visibleItemIds.forEach((id) => {
          const inst = tableData.find((item) => item._id === id);
          if (inst && isCreditExpired(inst)) {
            next.set(id, inst);
          }
        });
      }
      return next;
    });
  };

  const handleOpenBulkDelete = () => {
    if (selectedInstituteIds.size === 0) {
      swalWarning("Please select at least one expired institute using the checkboxes.");
      return;
    }

    const nonExpiredSelected = selectedInstitutesList.filter(
      (inst) => !isCreditExpired(inst),
    );
    if (nonExpiredSelected.length > 0) {
      swalWarning("Only expired institutes can be selected for deletion.");
      setSelectedInstituteIds((prev) => {
        const next = new Set(prev);
        nonExpiredSelected.forEach((inst) => next.delete(inst._id));
        return next;
      });
      return;
    }

    setBulkDeleteConfirmationText("");
    setBulkDeleteProgress({
      current: 0,
      total: selectedInstituteIds.size,
      currentName: "",
      statusMap: {},
      errorMap: {},
    });
    setIsBulkDeleteModalOpen(true);
  };

  const handleExecuteBulkDelete = async () => {
    if (bulkDeleteConfirmationText.trim().toUpperCase() !== "DELETE") {
      swalWarning('Please type "DELETE" to confirm deletion.');
      return;
    }

    const institutesToDelete = [...selectedInstitutesList];
    if (institutesToDelete.length === 0) {
      setIsBulkDeleteModalOpen(false);
      return;
    }

    setIsBulkDeleting(true);
    const initialStatusMap = {};
    institutesToDelete.forEach((inst) => {
      initialStatusMap[inst._id] = "pending";
    });

    setBulkDeleteProgress({
      current: 0,
      total: institutesToDelete.length,
      currentName: institutesToDelete[0]?.name || "",
      statusMap: initialStatusMap,
      errorMap: {},
    });

    const successfullyDeletedIds = new Set();
    const failedMap = {};

    for (let i = 0; i < institutesToDelete.length; i++) {
      const institute = institutesToDelete[i];
      const id = institute._id;
      const name = institute.name;

      setBulkDeleteProgress((prev) => ({
        ...prev,
        current: i,
        currentName: name,
        statusMap: {
          ...prev.statusMap,
          [id]: "deleting",
        },
      }));

      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/institute/delete/${id}`,
          {
            method: "DELETE",
            credentials: "include",
          },
        );

        if (!response) {
          throw new Error("Network error or failed to initiate delete request.");
        }

        if (!response.ok) {
          let errorMsg = `Status ${response.status}`;
          try {
            const errJson = await response.json();
            errorMsg = errJson.message || errorMsg;
          } catch (e) {
            // ignore
          }
          throw new Error(errorMsg);
        }

        successfullyDeletedIds.add(id);
        setBulkDeleteProgress((prev) => ({
          ...prev,
          current: i + 1,
          statusMap: {
            ...prev.statusMap,
            [id]: "success",
          },
        }));
      } catch (err) {
        failedMap[id] = err.message || "Deletion failed";
        setBulkDeleteProgress((prev) => ({
          ...prev,
          current: i + 1,
          statusMap: {
            ...prev.statusMap,
            [id]: "error",
          },
          errorMap: {
            ...prev.errorMap,
            [id]: err.message || "Deletion failed",
          },
        }));
      }
    }

    // Update table data, selected ids and usageMap
    if (successfullyDeletedIds.size > 0) {
      setSelectedInstituteIds(new Set());
      setSelectedInstitutesMap(new Map());
      setUsageMap((prev) => {
        const next = { ...prev };
        successfullyDeletedIds.forEach((id) => delete next[id]);
        return next;
      });
      fetchData(1, searchTerm, sortBy, sortOrder, filterType);
    }

    setIsBulkDeleting(false);

    const failedCount = Object.keys(failedMap).length;
    if (failedCount === 0) {
      setIsBulkDeleteModalOpen(false);
      setBulkDeleteConfirmationText("");
      swalSuccess(`Successfully deleted ${successfullyDeletedIds.size} institute(s).`);
    } else {
      setIsBulkDeleteModalOpen(false);
      setBulkDeleteConfirmationText("");
      swalWarning(
        `Deleted ${successfullyDeletedIds.size} institute(s). Failed to delete ${failedCount} institute(s).`,
      );
    }
  };

  const breadcrumbs = [
    { name: "Home", path: "/superadmin" },
    { name: "Manage Institutes", path: "/superadmin/manage-institutes", isLast: true }
  ];

  const handleDelete = async (id, name) => {
    const inst = tableData.find((item) => item._id === id);
    const subInfo = inst?.hasSubscribed ? " [Subscribed]" : "";
    const expiryInfo = inst?.gradingCreditExpiryDate
      ? ` | Expiry Date: ${displayDateOnly(inst.gradingCreditExpiryDate)}`
      : "";
    swalConfirm(
      `Are you sure you want to delete this institute?`,
      `${name} (${inst?.code || ""})${subInfo}${expiryInfo}\nwill be deleted permanently!`,
      "Yes, delete it!",
      "No, cancel!",
    ).then(async (result) => {
      if (result.isConfirmed) {
        setIsDeleting(true);
        setDeletingId(id);
        try {
          const response = await fetchWithAuth(
            `${serverUrl}/api/institute/delete/${id}`,
            {
              method: "DELETE",
              credentials: "include",
            },
          );

          if (!response) {
            swalError("Network error or failed to initiate delete request.");
            return;
          }

          if (!response.ok) {
            let errorMsg = `Failed to delete institute. Status: ${response.status}`;
            try {
              const errorData = await response.json();
              errorMsg = errorData.message || errorMsg;
            } catch (parseError) {
              swalError("An error occurred while deleting the institute.");
            }
            throw new Error(errorMsg);
          }

          setSelectedInstituteIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          setSelectedInstitutesMap((prev) => {
            const next = new Map(prev);
            next.delete(id);
            return next;
          });
          setUsageMap((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
          swalSuccess("Institute deleted successfully");
          if (tableData.length === 1 && page > 1) {
            setPage((prev) => Math.max(1, prev - 1));
          } else {
            fetchData(page, searchTerm, sortBy, sortOrder, filterType);
          }
        } catch (err) {
          swalError(
            err.message || "An unexpected error occurred during deletion.",
          );
        } finally {
          setIsDeleting(false);
          setDeletingId(null);
        }
      }
    });
  };

  const userDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    // Return only the date part in YYYY-MM-DD format
    return date.toISOString().slice(0, 10);
  };

  const convertToUTC = (dateStr) => {
    if (!dateStr) return ""; // Return empty string if no date
    // For date-only, we want to set it to start of day in UTC
    const date = new Date(dateStr + "T00:00:00.000Z");
    // Check if the date is valid before returning
    if (isNaN(date.getTime())) return "";
    return date.toISOString();
  };
  const displayDateOnly = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return new Date(date.getTime()).toLocaleDateString(navigator.language, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const computeCreditValuesOnEdit = (institute, currentForm) => {
    if (!institute) return currentForm;

    const instUsage = usageMap[institute._id];
    const updated = { ...currentForm };

    // ---- GRADING ----
    const gradingRenewCredits = parseInt(updated.gradingRenewCredits);
    const isGradingRenewActive =
      updated.gradingRenewCredits !== "" &&
      updated.gradingRenewCredits != null &&
      !isNaN(gradingRenewCredits) &&
      updated.gradingRenewDate !== "" &&
      updated.gradingRenewDate != null;

    const gradingBasePastPurchased = institute.combinedGradingBlockSize?.pastPurchased ?? 0;

    if (isGradingRenewActive) {
      const gradingUsed = instUsage?.combinedGrading?.usageSize ?? 0;
      const gradingTotal = instUsage?.combinedGrading?.purchasedSize ?? 0;
      const remainingGrading = Math.max(0, gradingTotal - gradingUsed);

      const expDateStr = updated.gradingCreditExpiryDate;
      let isGradingExpired = false;
      if (expDateStr) {
        const expDate = new Date(expDateStr + "T00:00:00.000Z");
        isGradingExpired = !isNaN(expDate.getTime()) && expDate < new Date();
      }

      updated.gradingPastPurchased = gradingBasePastPurchased + gradingUsed;
      updated.gradingAvailable = isGradingExpired
        ? gradingRenewCredits
        : gradingRenewCredits + remainingGrading;
    } else {
      updated.gradingAvailable = institute.combinedGradingBlockSize?.available ?? 0;
      updated.gradingPastPurchased = gradingBasePastPurchased;
    }

    // ---- QB ----
    const qbRenewCredits = parseInt(updated.qbRenewCredits);
    const isQbRenewActive =
      updated.qbRenewCredits !== "" &&
      updated.qbRenewCredits != null &&
      !isNaN(qbRenewCredits) &&
      updated.qbRenewDate !== "" &&
      updated.qbRenewDate != null;

    const qbBasePastPurchased = institute.questionBlockSize?.pastPurchased ?? 0;

    if (isQbRenewActive) {
      const qbUsed = instUsage?.questionBlock?.usageSize ?? 0;
      const qbTotal = instUsage?.questionBlock?.purchasedSize ?? 0;
      const remainingQb = Math.max(0, qbTotal - qbUsed);

      updated.qbPastPurchased = qbBasePastPurchased + qbUsed;
      updated.qbAvailable = qbRenewCredits + remainingQb;
    } else {
      updated.qbAvailable = institute.questionBlockSize?.available ?? 0;
      updated.qbPastPurchased = qbBasePastPurchased;
    }

    return updated;
  };

  const handleOpenCreditModal = (row) => {
    setSelectedCreditInstitute(row);
    setCreditMode("auto");
    const initialForm = {
      gradingCreditExpiryDate: row.gradingCreditExpiryDate ? userDate(row.gradingCreditExpiryDate) : "",
      gradingAvailable: row.combinedGradingBlockSize?.available ?? 0,
      gradingPastPurchased: row.combinedGradingBlockSize?.pastPurchased ?? 0,
      gradingLastPurchasedDate: row.combinedGradingBlockSize?.lastPurchasedDate
        ? userDate(row.combinedGradingBlockSize.lastPurchasedDate)
        : "",
      gradingLastPurchasedCredits:
        row.combinedGradingBlockSize?.lastPurchasedCredits != null
          ? String(row.combinedGradingBlockSize.lastPurchasedCredits)
          : "",
      gradingRenewCredits: "",
      gradingRenewDate: "",
      qbAvailable: row.questionBlockSize?.available ?? 0,
      qbPastPurchased: row.questionBlockSize?.pastPurchased ?? 0,
      qbLastPurchasedDate: row.questionBlockSize?.lastPurchasedDate
        ? userDate(row.questionBlockSize.lastPurchasedDate)
        : "",
      qbLastPurchasedCredits:
        row.questionBlockSize?.lastPurchasedCredits != null
          ? String(row.questionBlockSize.lastPurchasedCredits)
          : "",
      qbRenewCredits: "",
      qbRenewDate: "",
    };

    setCreditFormData(initialForm);
    setCreditFormError("");
    setIsCreditModalOpen(true);
  };

  const handleModeChange = (newMode) => {
    setCreditMode(newMode);
    if (newMode === "auto" && selectedCreditInstitute) {
      setCreditFormData((prev) => computeCreditValuesOnEdit(selectedCreditInstitute, prev));
    }
  };

  const handleCreditInputChange = (e) => {
    const { name, value } = e.target;
    setCreditFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (creditMode === "auto" && selectedCreditInstitute) {
        return computeCreditValuesOnEdit(selectedCreditInstitute, updated);
      }
      return updated;
    });
  };

  const handleSaveCredit = async (e) => {
    e.preventDefault();
    if (!selectedCreditInstitute) return;
    setCreditFormError("");
    setIsCreditSubmitting(true);

    try {
      const updatedGradingCreditExpiryDate = creditFormData.gradingCreditExpiryDate
        ? convertToUTC(creditFormData.gradingCreditExpiryDate)
        : null;

      const updatedCombinedGradingBlockSize = {
        available: parseInt(creditFormData.gradingAvailable) || 0,
        pastPurchased: parseInt(creditFormData.gradingPastPurchased) || 0,
        lastPurchasedDate:
          creditMode === "auto" && creditFormData.gradingRenewDate
            ? convertToUTC(creditFormData.gradingRenewDate)
            : creditFormData.gradingLastPurchasedDate
            ? convertToUTC(creditFormData.gradingLastPurchasedDate)
            : null,
        lastPurchasedCredits:
          creditMode === "auto" && creditFormData.gradingRenewCredits !== ""
            ? parseInt(creditFormData.gradingRenewCredits) || 0
            : parseInt(creditFormData.gradingLastPurchasedCredits) || 0,
      };

      const updatedQuestionBlockSize = {
        available: parseInt(creditFormData.qbAvailable) || 0,
        pastPurchased: parseInt(creditFormData.qbPastPurchased) || 0,
        lastPurchasedDate:
          creditMode === "auto" && creditFormData.qbRenewDate
            ? convertToUTC(creditFormData.qbRenewDate)
            : creditFormData.qbLastPurchasedDate
            ? convertToUTC(creditFormData.qbLastPurchasedDate)
            : null,
        lastPurchasedCredits:
          creditMode === "auto" && creditFormData.qbRenewCredits !== ""
            ? parseInt(creditFormData.qbRenewCredits) || 0
            : parseInt(creditFormData.qbLastPurchasedCredits) || 0,
      };

      const requestData = {
        name: selectedCreditInstitute.name,
        tagline: selectedCreditInstitute.tagline ?? "",
        address: selectedCreditInstitute.address ?? "",
        code: selectedCreditInstitute.code,
        domain: selectedCreditInstitute.domain ?? "",
        category: selectedCreditInstitute.category ?? "",
        standardizedTestTemplateIds:
          selectedCreditInstitute.standardizedTestTemplateIds || [],
        curriculum: selectedCreditInstitute.curriculum ?? "",
        reportCardTemplate:
          selectedCreditInstitute.reportCardTemplate || "standard",
        gradingCreditExpiryDate: updatedGradingCreditExpiryDate,
        phoneNumber: selectedCreditInstitute.phoneNumber ?? "",
        websiteURL: selectedCreditInstitute.websiteURL ?? "",
        whatsAppAccessToken: selectedCreditInstitute.whatsAppAccessToken ?? "",
        whatsAppPhoneNumberId:
          selectedCreditInstitute.whatsAppPhoneNumberId ?? "",
        whatsAppNotificationLimit:
          selectedCreditInstitute.whatsAppNotificationLimit || 0,
        dataRegion: selectedCreditInstitute.dataRegion ?? "",
        webhookEnabled: selectedCreditInstitute.webhookEnabled ?? false,
        isImageCreationAllowed:
          selectedCreditInstitute.isImageCreationAllowed ?? false,
        isExternalBatchProcessing:
          selectedCreditInstitute.isExternalBatchProcessing ?? false,
        isManualGradingAllowed:
          selectedCreditInstitute.isManualGradingAllowed ?? false,
        isOnlineExamAllowed:
          selectedCreditInstitute.isOnlineExamAllowed ?? false,
        isAdvancedOnlineExamAllowed:
          selectedCreditInstitute.isAdvancedOnlineExamAllowed ?? false,
        hasSubscribed: selectedCreditInstitute.hasSubscribed ?? false,
        allowStudentClassJoin:
          selectedCreditInstitute.allowStudentClassJoin ?? false,
        autoGenerateStudentPassword:
          selectedCreditInstitute.autoGenerateStudentPassword ?? false,
        allowHomeworkGrading:
          selectedCreditInstitute.allowHomeworkGrading ?? false,
        webhookURL: selectedCreditInstitute.webhookURL ?? "",
        combinedGradingBlockSize: updatedCombinedGradingBlockSize,
        questionBlockSize: updatedQuestionBlockSize,
        pageRangeCreditUsageRules:
          selectedCreditInstitute.pageRangeCreditUsageRules || [],
        isCOPOEnabled: Boolean(selectedCreditInstitute.COPOConfig),
        isQRPageCountDetectionEnabled: Boolean(
          selectedCreditInstitute.isQRPageCountDetectionEnabled,
        ),
        isReportCardEnabled:
          selectedCreditInstitute.isReportCardEnabled !== false,
        COPOConfig: selectedCreditInstitute.COPOConfig || null,
        feedbackMode:
          selectedCreditInstitute.feedbackMode || "APP_FEEDBACK_ONLY",
        sla: selectedCreditInstitute.sla || { maxProcessingHours: null },
      };

      const form = new FormData();
      Object.keys(requestData).forEach((key) => {
        if (
          key === "combinedGradingBlockSize" ||
          key === "questionBlockSize" ||
          key === "pageRangeCreditUsageRules" ||
          key === "COPOConfig" ||
          key === "sla" ||
          key === "standardizedTestTemplateIds"
        ) {
          form.append(key, JSON.stringify(requestData[key]));
        } else {
          form.append(key, requestData[key]);
        }
      });

      const response = await fetchWithAuth(
        `${serverUrl}/api/institute/update/${selectedCreditInstitute._id}`,
        {
          method: "PUT",
          credentials: "include",
          body: form,
        },
      );

      if (!response) return;

      if (!response.ok) {
        const error = await response.json();
        throw new Error(
          error.message || "Failed to update institute credits",
        );
      }

      const updatedInstitute = await response.json();

      setTableData((prevData) =>
        prevData.map((inst) =>
          inst._id === updatedInstitute._id ? updatedInstitute : inst,
        ),
      );

      swalSuccess("Institute credits updated successfully");
      setIsCreditModalOpen(false);
    } catch (error) {
      setCreditFormError(
        error.message || "Failed to update institute credits",
      );
      swalError(error.message || "Failed to update institute credits");
    } finally {
      setIsCreditSubmitting(false);
    }
  };

  const fetchDataRegions = async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/data-region/get-all`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      if (!response) return;

      const data = await response.json();
      const dataRegionOptions = data.map((item) => ({
        label: item.countryName,
        value: item.countryCode,
      }));
      setDataRegionOptions(dataRegionOptions);
    } catch (error) {
      console.error("Failed to fetch data regions");
    }
  };

  const [aiFeatureOptions, setAIFeatureOptions] = useState({ features: [], configs: [] });

  const fetchAIFeatureOptions = async () => {
    try {
      const response = await fetchWithAuth(
        serverUrl + "/api/institute/ai-feature-options",
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      if (!response || !response.ok) return;
      const data = await response.json();
      setAIFeatureOptions({
        features: data.features || [],
        configs: data.configs || [],
      });
    } catch (error) {
      console.error("Failed to fetch AI feature options", error);
    }
  };

  useEffect(() => {
    fetchDataRegions();
    fetchAIFeatureOptions();
  }, []);

  const handleCreateInstitute = async (e) => {
    e.preventDefault();
    setFormError("");
    setIsSubmitting(true);

    try {
      // Validate pageRangeCreditUsageRules only if enabled
      if (formData.isPageRangeRulesEnabled) {
        const rules = formData.pageRangeCreditUsageRules;
        for (let i = 0; i < rules.length; i++) {
          const rule = rules[i];
          if (!rule.maxPageCount || !rule.creditUsage) {
            setFormError(
              "Please fill all fields in Page Range Credit Usage Rules.",
            );
            return;
          }
          if (parseInt(rule.maxPageCount) <= parseInt(rule.minPageCount)) {
            setFormError("Max Page Count must be greater than Min Page Count.");
            return;
          }
          if (
            i > 0 &&
            parseInt(rule.minPageCount) !==
              parseInt(rules[i - 1].maxPageCount) + 1
          ) {
            setFormError("Min Page Count must be consecutive.");
            return;
          }
        }
      }

      // Validate CO Weightages if CO-PO is enabled
      if (formData.isCOPOEnabled) {
        const coWeightageValidation = validateCOWeightage(
          formData.internalCOWeightage,
          formData.externalCOWeightage,
        );
        if (!coWeightageValidation.isValid) {
          setFormError(coWeightageValidation.message);
          swalError(coWeightageValidation.message);
          return;
        }
        const directIndirectValidation = validateDirectIndirectCOWeightage(
          formData.directCOWeightage,
          formData.indirectCOWeightage,
        );
        if (!directIndirectValidation.isValid) {
          setFormError(directIndirectValidation.message);
          swalError(directIndirectValidation.message);
          return;
        }
        const poWeightageValidation = validateDirectIndirectPOWeightage(
          formData.directPOWeightage,
          formData.indirectPOWeightage,
        );
        if (!poWeightageValidation.isValid) {
          setFormError(poWeightageValidation.message);
          swalError(poWeightageValidation.message);
          return;
        }
      }

      if (Array.isArray(formData.aiProviderConfig)) {
        for (const item of formData.aiProviderConfig) {
          if (item.feature && !item.aiModelConfigId) {
            swalWarning("Incomplete AI Config", `Please select an AI model configuration for feature '${item.feature}'`);
            return;
          }
        }
      }

      // Create the request data with nested objects
      const requestData = {
        name: formData.name,
        tagline: formData.tagline ?? "",
        address: formData.address ?? "",
        code: formData.code,
        domain: formData.domain,
        category: formData.category,
        standardizedTestTemplateIds: formData.standardizedTestTemplateIds || [],
        curriculum: formData.curriculum,
        reportCardTemplate: formData.reportCardTemplate || "standard",
  
        phoneNumber: formData.phoneNumber,
        websiteURL: formData.websiteURL,
        whatsAppAccessToken: formData.whatsAppAccessToken,
        whatsAppPhoneNumberId: formData.whatsAppPhoneNumberId,
        whatsAppNotificationLimit: parseInt(formData.whatsAppNotificationLimit) || 0,
        dataRegion: formData.dataRegion,
        webhookEnabled: formData.webhookEnabled,
        isImageCreationAllowed: formData.isImageCreationAllowed,
        isExternalBatchProcessing: formData.isExternalBatchProcessing,
        isManualGradingAllowed: formData.isManualGradingAllowed,
        isOnlineExamAllowed: formData.isOnlineExamAllowed,
        isAdvancedOnlineExamAllowed: formData.isAdvancedOnlineExamAllowed,
        hasSubscribed: formData.hasSubscribed,
        allowStudentClassJoin: formData.allowStudentClassJoin,
        autoGenerateStudentPassword: formData.autoGenerateStudentPassword,
        allowHomeworkGrading: Boolean(formData.allowHomeworkGrading),
        webhookURL: formData.webhookURL,
        combinedGradingBlockSize: {
          available: 0,
          pastPurchased: 0,
          lastPurchasedDate: null,
          lastPurchasedCredits: 0,
        },
        questionBlockSize: {
          available: 0,
          pastPurchased: 0,
          lastPurchasedDate: null,
          lastPurchasedCredits: 0,
        },
        pageRangeCreditUsageRules: formData.isPageRangeRulesEnabled
          ? formData.pageRangeCreditUsageRules.map((rule) => ({
              minPageCount: parseInt(rule.minPageCount),
              maxPageCount: parseInt(rule.maxPageCount),
              creditUsage: parseInt(rule.creditUsage),
            }))
          : [],
        isCOPOEnabled: formData.isCOPOEnabled,
        isQRPageCountDetectionEnabled: formData.isQRPageCountDetectionEnabled,
        isReportCardEnabled: formData.isReportCardEnabled,
        isCreditReportEnabled: formData.isCreditReportEnabled,
        COPOConfig: formData.isCOPOEnabled
          ? {
              coAttainmentMethod: formData.coAttainmentMethod,
              scoreAveragingBasis: formData.scoreAveragingBasis,
              scoreBasedAttainmentCriteria:
                formData.coAttainmentMethod === "SCORE_BASED_ATTAINMENT"
                  ? {
                      level1MinScorePercentage:
                        parseFloat(formData.level1MinScorePercentage) || 0,
                      level2MinScorePercentage:
                        parseFloat(formData.level2MinScorePercentage) || 0,
                      level3MinScorePercentage:
                        parseFloat(formData.level3MinScorePercentage) || 0,
                    }
                  : undefined,
              thresholdBasedAttainmentCriteria:
                formData.coAttainmentMethod === "THRESHOLD_BASED_ATTAINMENT"
                  ? {
                      minScorePercentage:
                        parseFloat(formData.minScorePercentage) || 0,
                      level1MinStudentPercentage:
                        parseFloat(formData.level1MinStudentPercentage) || 0,
                      level2MinStudentPercentage:
                        parseFloat(formData.level2MinStudentPercentage) || 0,
                      level3MinStudentPercentage:
                        parseFloat(formData.level3MinStudentPercentage) || 0,
                    }
                  : undefined,
              internalCOWeightage:
                parseFloat(formData.internalCOWeightage) || 0,
              externalCOWeightage:
                parseFloat(formData.externalCOWeightage) || 0,
              directCOWeightage: parseFloat(formData.directCOWeightage) || 0,
              indirectCOWeightage:
                parseFloat(formData.indirectCOWeightage) || 0,
              directPOWeightage: parseFloat(formData.directPOWeightage) || 0,
              indirectPOWeightage:
                parseFloat(formData.indirectPOWeightage) || 0,
            }
          : null,
        feedbackMode: formData.feedbackMode || "APP_FEEDBACK_ONLY",
        sla: {
          maxProcessingHours: parseFloat(formData.slaMaxProcessingHours) || null,
        },
        annotationPrintingOffsetConfig: Array.isArray(
          formData.annotationPrintingOffsetConfig,
        )
          ? formData.annotationPrintingOffsetConfig
              .filter(
                (item) =>
                  item.name || item.topOffset !== "" || item.rightOffset !== "",
              )
              .map((item) => ({
                name: item.name || "",
                topOffset:
                  item.topOffset !== "" ? parseFloat(item.topOffset) || 0 : 0,
                rightOffset:
                  item.rightOffset !== ""
                    ? parseFloat(item.rightOffset) || 0
                    : 0,
              }))
          : [],
        aiProviderConfig: Array.isArray(formData.aiProviderConfig)
          ? formData.aiProviderConfig
              .filter((item) => item.feature && item.feature.trim())
              .map((item) => ({
                feature: item.feature.trim(),
                aiModelConfigId: item.aiModelConfigId || undefined,
              }))
          : [],
      };

      const form = new FormData();
      // Append all fields to FormData
      Object.keys(requestData).forEach((key) => {
        const value = requestData[key];

        // Skip only undefined / null (not false, 0, etc.)
        if (value == null) return;
        if (
          key === "combinedGradingBlockSize" ||
          key === "questionBlockSize" ||
          key === "pageRangeCreditUsageRules" ||
          key === "COPOConfig" ||
          key === "sla" ||
          key === "standardizedTestTemplateIds" ||
          key === "annotationPrintingOffsetConfig" ||
          key === "aiProviderConfig"
        ) {
          // Manually stringify objects/array for backend to parse properly
          form.append(key, JSON.stringify(requestData[key]));
        } else {
          form.append(key, requestData[key]);
        }
      });
      form.append("logo", formData.logo);

      const response = await fetchWithAuth(`${serverUrl}/api/institute/add`, {
        method: "POST",
        credentials: "include",
        body: form,
      });

      if (!response) return;

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.errors && Array.isArray(errorData.errors)) {
          swalError(
            "Validation Error",
            errorData.errors.map((error) => `<p>${error}</p>`).join(""),
          );
          return;
        } else {
          throw new Error(errorData.message || "Failed to create institute");
        }
      } else {
        const newInstitute = await response.json();
        if (newInstitute.code !== "grademe-ai") {
          fetchData(1, searchTerm, sortBy, sortOrder, filterType);
        }
        setIsCreateModalOpen(false);
        setFormData({
          ...formData,
          name: "",
          tagline: "",
          address: "",
          code: "",
          domain: "",
          category: "",
          standardizedTestTemplateIds: [],
          reportCardTemplate: "standard",
          gradingCreditExpiryDate: "",
          phoneNumber: "",
          formattedPhoneNumber: "",
          country: "",
          websiteURL: "",
          logo: null,
          combinedGradingBlockSize: "",
          questionBlockSize: "",
          whatsAppAccessToken: "",
          whatsAppPhoneNumberId: "",
          whatsAppNotificationLimit: "",
          isPageRangeRulesEnabled: false,
          pageRangeCreditUsageRules: [
            { minPageCount: 1, maxPageCount: "", creditUsage: "" },
          ],
          isImageCreationAllowed: false,
          isExternalBatchProcessing: false,
          hasSubscribed: false,
          allowStudentClassJoin: false,
          autoGenerateStudentPassword: false,
          allowHomeworkGrading: false,
          isReportCardEnabled: false,
          annotationPrintingOffsetConfig: [],
          aiProviderConfig: [],
          coAttainmentMethod: defaultCOPOConfig.coAttainmentMethod,
          // SCORE_BASED_ATTAINMENT criteria
          level1MinScorePercentage:
            defaultCOPOConfig.scoreBasedAttainment.level1MinScorePercentage,
          level2MinScorePercentage:
            defaultCOPOConfig.scoreBasedAttainment.level2MinScorePercentage,
          level3MinScorePercentage:
            defaultCOPOConfig.scoreBasedAttainment.level3MinScorePercentage,
          scoreAveragingBasis: defaultCOPOConfig.scoreAveragingBasis,
          // THRESHOLD_BASED_ATTAINMENT criteria
          minScorePercentage:
            defaultCOPOConfig.thresholdBasedAttainment.minScorePercentage,
          level1MinStudentPercentage:
            defaultCOPOConfig.thresholdBasedAttainment
              .level1MinStudentPercentage,
          level2MinStudentPercentage:
            defaultCOPOConfig.thresholdBasedAttainment
              .level2MinStudentPercentage,
          level3MinStudentPercentage:
            defaultCOPOConfig.thresholdBasedAttainment
              .level3MinStudentPercentage,
          // Weightages
          internalCOWeightage: defaultCOPOConfig.internalCOWeightage,
          externalCOWeightage: defaultCOPOConfig.externalCOWeightage,
          directCOWeightage: defaultCOPOConfig.directCOWeightage,
          indirectCOWeightage: defaultCOPOConfig.indirectCOWeightage,
          directPOWeightage: defaultCOPOConfig.directPOWeightage,
          indirectPOWeightage: defaultCOPOConfig.indirectPOWeightage,
          feedbackMode: "APP_FEEDBACK_ONLY",
          slaMaxProcessingHours: "",
        });
        swalSuccess("Institute created successfully");
      }
    } catch (err) {
      setFormError(err.message);
      swalError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateAIModelConfig = async (e) => {
    e.preventDefault();
    setFormError("");
    setIsSubmitting(true);

    const configData = {
      provider: (aiProvider || "gemini").toLowerCase(),
      model: aiModel || "",
      type: aiModelUseType || "",
      inputCostPerMillionTokens: formData.inputCostPerMillionTokens ?? 0.0,
      outputCostPerMillionTokens: formData.outputCostPerMillionTokens ?? 0.0,
      thinkingCostPerMillionTokens:
        formData.thinkingCostPerMillionTokens ?? 0.0,
      temperature: formData.temperature,
      topK: formData.topK,
      topP: formData.topP,
      frequencyPenalty: formData.frequencyPenalty,
      presencePenalty: formData.presencePenalty,
    };

    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/institute/addAIModelConfig`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(configData),
        },
      );
      if (!response) return;

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.errors && Array.isArray(errorData.errors)) {
          swalError(
            "Validation Error",
            errorData.errors.map((error) => `<p>${error}</p>`).join(""),
          );
          return;
        } else {
          throw new Error(
            errorData.message || "Failed to create AI Model Configuration",
          );
        }
      } else {
        setIsCreateAIConfigModalOpen(false);
        setFormData({
          ...formData,
          temperature: "",
          topK: "",
          topP: "",
          frequencyPenalty: "",
          presencePenalty: "",
        });
        setAiProvider("gemini");
        setAiModel(null);
        setAiModelUseType(null);
        swalSuccess("Config created successfully");
      }
    } catch (err) {
      setFormError(err.message);
      swalError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const refreshInstituteUsage = async (instituteId) => {
    try {
      // Find the institute to check if it's subscribed
      const institute = tableData.find((inst) => inst._id === instituteId);
      if (!institute || !institute.hasSubscribed) {
        // Remove from usage map if not subscribed
        setUsageMap((prev) => {
          const newMap = { ...prev };
          delete newMap[instituteId];
          return newMap;
        });
        return;
      }

      const response = await fetchWithAuth(
        `${serverUrl}/api/institute/get-usage/${instituteId}`,
        {
          credentials: "include",
        },
      );
      if (response.ok) {
        const usageData = await response.json();
        setUsageMap((prev) => ({
          ...prev,
          [instituteId]: usageData,
        }));
      }
    } catch (err) {
      console.error("Failed to refresh usage data:", err);
    }
  };

  const handleEditInstitute = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!isEditChanged) {
      swalWarning("No changes detected");
      return;
    }
    setIsSubmitting(true);

    try {
      // Validate pageRangeCreditUsageRules only if enabled
      if (editFormData.isPageRangeRulesEnabled) {
        const rules = editFormData.pageRangeCreditUsageRules;
        for (let i = 0; i < rules.length; i++) {
          const rule = rules[i];
          if (!rule.maxPageCount || !rule.creditUsage) {
            setFormError(
              "Please fill all fields in Page Range Credit Usage Rules.",
            );
            return;
          }
          if (parseInt(rule.maxPageCount) <= parseInt(rule.minPageCount)) {
            setFormError("Max Page Count must be greater than Min Page Count.");
            return;
          }
          if (
            i > 0 &&
            parseInt(rule.minPageCount) !==
              parseInt(rules[i - 1].maxPageCount) + 1
          ) {
            setFormError("Min Page Count must be consecutive.");
            return;
          }
        }
      }

      // Validate CO Weightages if CO-PO is enabled
      if (editFormData.isCOPOEnabled) {
        const coWeightageValidation = validateCOWeightage(
          editFormData.internalCOWeightage,
          editFormData.externalCOWeightage,
        );
        if (!coWeightageValidation.isValid) {
          setFormError(coWeightageValidation.message);
          swalError(coWeightageValidation.message);
          return;
        }
        const directIndirectValidation = validateDirectIndirectCOWeightage(
          editFormData.directCOWeightage,
          editFormData.indirectCOWeightage,
        );
        if (!directIndirectValidation.isValid) {
          setFormError(directIndirectValidation.message);
          swalError(directIndirectValidation.message);
          return;
        }
        const poWeightageValidation = validateDirectIndirectPOWeightage(
          editFormData.directPOWeightage,
          editFormData.indirectPOWeightage,
        );
        if (!poWeightageValidation.isValid) {
          setFormError(poWeightageValidation.message);
          swalError(poWeightageValidation.message);
          return;
        }
      }

      if (Array.isArray(editFormData.aiProviderConfig)) {
        for (const item of editFormData.aiProviderConfig) {
          if (item.feature && !item.aiModelConfigId) {
            swalWarning("Incomplete AI Config", `Please select an AI model configuration for feature '${item.feature}'`);
            return;
          }
        }
      }

      // Create the request data with nested objects
      const requestData = {
        name: editFormData.name,
        tagline: editFormData.tagline ?? "",
        address: editFormData.address ?? "",
        code: editFormData.code,
        domain: editFormData.domain ?? "",
        category: editFormData.category ?? "",
        standardizedTestTemplateIds: editFormData.standardizedTestTemplateIds || [],
        curriculum: editFormData.curriculum ?? "",
        reportCardTemplate: editFormData.reportCardTemplate || "standard",
        phoneNumber: editFormData.phoneNumber ?? "",
        websiteURL: editFormData.websiteURL ?? "",
        whatsAppAccessToken: editFormData.whatsAppAccessToken ?? "",
        whatsAppPhoneNumberId: editFormData.whatsAppPhoneNumberId ?? "",
        whatsAppNotificationLimit: parseInt(editFormData.whatsAppNotificationLimit) || 0,
        dataRegion: editFormData.dataRegion ?? "",
        webhookEnabled: editFormData.webhookEnabled ?? "",
        isImageCreationAllowed: editFormData.isImageCreationAllowed,
        isExternalBatchProcessing:
          editFormData.isExternalBatchProcessing ?? false,
        isManualGradingAllowed: editFormData.isManualGradingAllowed ?? false,
        isOnlineExamAllowed: editFormData.isOnlineExamAllowed ?? false,
        isAdvancedOnlineExamAllowed: editFormData.isAdvancedOnlineExamAllowed ?? false,
        hasSubscribed: editFormData.hasSubscribed,
        allowStudentClassJoin: editFormData.allowStudentClassJoin ?? false,
        autoGenerateStudentPassword: editFormData.autoGenerateStudentPassword ?? false,
        allowHomeworkGrading: Boolean(editFormData.allowHomeworkGrading),
        webhookURL: editFormData.webhookURL ?? "",
        combinedGradingBlockSize: editFormData.combinedGradingBlockSize || {
          available: 0,
          pastPurchased: 0,
          lastPurchasedDate: null,
          lastPurchasedCredits: 0,
        },
        questionBlockSize: editFormData.questionBlockSize || {
          available: 0,
          pastPurchased: 0,
          lastPurchasedDate: null,
          lastPurchasedCredits: 0,
        },
        pageRangeCreditUsageRules: editFormData.isPageRangeRulesEnabled
          ? editFormData.pageRangeCreditUsageRules.map((rule) => ({
              minPageCount: parseInt(rule.minPageCount),
              maxPageCount: parseInt(rule.maxPageCount),
              creditUsage: parseInt(rule.creditUsage),
            }))
          : [],
        isCOPOEnabled: editFormData.isCOPOEnabled,
        isQRPageCountDetectionEnabled: editFormData.isQRPageCountDetectionEnabled,
        isReportCardEnabled: editFormData.isReportCardEnabled,
        isCreditReportEnabled: editFormData.isCreditReportEnabled,
        COPOConfig: editFormData.isCOPOEnabled
          ? {
              coAttainmentMethod: editFormData.coAttainmentMethod,
              scoreAveragingBasis: editFormData.scoreAveragingBasis,
              scoreBasedAttainmentCriteria:
                editFormData.coAttainmentMethod === "SCORE_BASED_ATTAINMENT"
                  ? {
                      level1MinScorePercentage:
                        parseFloat(editFormData.level1MinScorePercentage) || 0,
                      level2MinScorePercentage:
                        parseFloat(editFormData.level2MinScorePercentage) || 0,
                      level3MinScorePercentage:
                        parseFloat(editFormData.level3MinScorePercentage) || 0,
                    }
                  : undefined,
              thresholdBasedAttainmentCriteria:
                editFormData.coAttainmentMethod === "THRESHOLD_BASED_ATTAINMENT"
                  ? {
                      minScorePercentage:
                        parseFloat(editFormData.minScorePercentage) || 0,
                      level1MinStudentPercentage:
                        parseFloat(editFormData.level1MinStudentPercentage) ||
                        0,
                      level2MinStudentPercentage:
                        parseFloat(editFormData.level2MinStudentPercentage) ||
                        0,
                      level3MinStudentPercentage:
                        parseFloat(editFormData.level3MinStudentPercentage) ||
                        0,
                    }
                  : undefined,
              internalCOWeightage:
                parseFloat(editFormData.internalCOWeightage) || 0,
              externalCOWeightage:
                parseFloat(editFormData.externalCOWeightage) || 0,
              directCOWeightage:
                parseFloat(editFormData.directCOWeightage) || 0,
              indirectCOWeightage:
                parseFloat(editFormData.indirectCOWeightage) || 0,
              directPOWeightage:
                parseFloat(editFormData.directPOWeightage) || 0,
              indirectPOWeightage:
                parseFloat(editFormData.indirectPOWeightage) || 0,
            }
          : null,
        feedbackMode: editFormData.feedbackMode || "APP_FEEDBACK_ONLY",
        sla: {
          maxProcessingHours: parseFloat(editFormData.slaMaxProcessingHours) || null,
        },
        annotationPrintingOffsetConfig: Array.isArray(
          editFormData.annotationPrintingOffsetConfig,
        )
          ? editFormData.annotationPrintingOffsetConfig
              .filter(
                (item) =>
                  item.name || item.topOffset !== "" || item.rightOffset !== "",
              )
              .map((item) => ({
                ...(item._id ? { _id: item._id } : {}),
                name: item.name || "",
                topOffset:
                  item.topOffset !== "" ? parseFloat(item.topOffset) || 0 : 0,
                rightOffset:
                  item.rightOffset !== ""
                    ? parseFloat(item.rightOffset) || 0
                    : 0,
              }))
          : [],
        aiProviderConfig: Array.isArray(editFormData.aiProviderConfig)
          ? editFormData.aiProviderConfig
              .filter((item) => item.feature && item.feature.trim())
              .map((item) => ({
                feature: item.feature.trim(),
                aiModelConfigId: item.aiModelConfigId || undefined,
              }))
          : [],
      };

      const form = new FormData();
      // Append all fields to FormData
      Object.keys(requestData).forEach((key) => {
        if (
          key === "combinedGradingBlockSize" ||
          key === "questionBlockSize" ||
          key === "pageRangeCreditUsageRules" ||
          key === "COPOConfig" ||
          key === "sla" ||
          key === "standardizedTestTemplateIds" ||
          key === "annotationPrintingOffsetConfig" ||
          key === "aiProviderConfig"
        ) {
          // Manually stringify objects/array for backend to parse properly
          form.append(key, JSON.stringify(requestData[key]));
        } else {
          form.append(key, requestData[key]);
        }
      });

      if (editFormData.logo) {
        form.append("logo", editFormData.logo);
      }

      const response = await fetchWithAuth(
        `${serverUrl}/api/institute/update/${editFormData.id}`,
        {
          method: "PUT",
          credentials: "include",
          body: form,
        },
      );

      if (!response) return;

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to update institute");
      }

      const updatedInstitute = await response.json();

      // Update the table data properly
      if (updatedInstitute.code !== "grademe-ai") {
        // If the updated institute should be shown in the table, update it
        setTableData((prevData) =>
          prevData.map((institute) =>
            institute._id === updatedInstitute._id
              ? updatedInstitute
              : institute,
          ),
        );
        // Refresh usage data for the updated institute
        await refreshInstituteUsage(updatedInstitute._id);
      } else {
        // If the updated institute should not be shown (code is "grademe-ai"), remove it
        setTableData((prevData) =>
          prevData.filter(
            (institute) => institute._id !== updatedInstitute._id,
          ),
        );
        // Remove from usage map as well
        setUsageMap((prev) => {
          const newMap = { ...prev };
          delete newMap[updatedInstitute._id];
          return newMap;
        });
      }

      setIsEditModalOpen(false);
      setEditFormData(defaultEditFormData);
      setInitialEditFormData(null);
      swalSuccess("Institute updated successfully");
    } catch (err) {
      setFormError(err.message);
      swalError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns = [
    { key: "code", label: "Institute Code", sortable: true },
    {
      key: "name",
      label: "Institute Name",
      sortable: true,
      render: (row) => (
        <div>
          <div style={{ fontWeight: 600, color: "#1e293b" }}>{row.name}</div>
          {row.tagline && (
            <div style={{ fontSize: "11px", color: "#64748b", fontStyle: "italic", marginTop: "2px" }}>
              {row.tagline}
            </div>
          )}
          {row.address && (
            <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px", display: "flex", alignItems: "center", gap: "4px" }}>
              <i className="fa-solid fa-location-dot" style={{ fontSize: "10px", color: "#94a3b8" }}></i>
              <span>{row.address}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "GradingCreditUsage",
      label: "Grading Credit Usage",
      sortable: true,
      sortBy: (row) =>
        row.hasSubscribed
          ? usageMap[row._id]?.combinedGrading?.usageSize || 0
          : -1,
      render: (row) => {
        const { text, className } = getUsageDisplay(
          usageMap[row._id]?.combinedGrading,
          row.hasSubscribed,
        );
        return <span className={className}>{text}</span>;
      },
    },
    {
      key: "questionBankCreditUsage",
      label: "Question Bank Credit Usage",
      sortable: true,
      sortBy: (row) =>
        row.hasSubscribed
          ? usageMap[row._id]?.questionBlock?.usageSize || 0
          : -1,
      render: (row) => {
        const { text, className } = getUsageDisplay(
          usageMap[row._id]?.questionBlock,
          row.hasSubscribed,
        );
        return <span className={className}>{text}</span>;
      },
    },
    {
      key: "gradingCreditExpiryDate",
      label: "Credit Expiry Date",
      sortable: true,
      sortBy: (row) =>
        row.gradingCreditExpiryDate
          ? new Date(row.gradingCreditExpiryDate).getTime()
          : 0,
      render: (row) => {
        const expired = isCreditExpired(row);
        return (
          <span
            style={{
              color: expired ? "#dc2626" : "inherit",
              fontWeight: expired ? 600 : "normal",
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            {expired && (
              <i
                className="fa-solid fa-triangle-exclamation"
                style={{ fontSize: "0.8rem", color: "#dc2626" }}
                title="Credit Expired"
              ></i>
            )}
            {displayDateOnly(row.gradingCreditExpiryDate) || "-"}
          </span>
        );
      },
    },
    {
      key: "admins",
      label: "Admins",
      sortable: false,
      render: (row) => (
        <Link to={`/superadmin/manage-admins/${row._id}`} className="view-btn">
          <i className="fa-solid fa-user"></i>
        </Link>
      ),
    },
    {
      key: "token-usage",
      label: "Token Usage",
      sortable: false,
      render: (row) => (
        <Link
          to={`/superadmin/manage-token-usage/${row._id}`}
          className="view-btn"
        >
          <i className="fa-solid fa-dollar-sign"></i>
        </Link>
      ),
    },
    {
      key: "contract",
      label: "contract",
      sortable: false,
      render: (row) => (
        <Link
          to={`/superadmin/manage-contract/${row._id}`}
          className="view-btn"
        >
          <i className="fa-solid fa-file-signature"></i>
        </Link>
      ),
    },
    {
      key: "manageCredit",
      label: "Manage Credit",
      sortable: false,
      render: (row) => (
        <button
          className="edit-btn"
          title="Manage Credit"
          onClick={() => handleOpenCreditModal(row)}
        >
          <i className="fa-solid fa-coins"></i>
        </button>
      ),
    },
    {
      key: "edit",
      label: "Edit",
      sortable: false,
      render: (row) => (
        <button
          className="edit-btn"
          onClick={() => {
            const rules =
              row.pageRangeCreditUsageRules &&
              row.pageRangeCreditUsageRules.length > 0
                ? row.pageRangeCreditUsageRules
                : [{ minPageCount: 1, maxPageCount: "", creditUsage: "" }];
            setOriginalPageRangeRules(rules);
            const initialFormState = {
              id: row._id,
              name: row.name || "",
              tagline: row.tagline || "",
              address: row.address || "",
              code: row.code || "",
              domain: row.domain || "",
              category: row.category || "",
              standardizedTestTemplateIds: Array.isArray(
                row.standardizedTestTemplateIds,
              )
                ? row.standardizedTestTemplateIds.map((t) =>
                    typeof t === "object" ? t._id : t,
                  )
                : [],
              curriculum: row.curriculum || "",
              reportCardTemplate: row.reportCardTemplate || "standard",
              gradingCreditExpiryDate: row.gradingCreditExpiryDate || null,
              phoneNumber: row.phoneNumber || "",
              formattedPhoneNumber: row.phoneNumber || "",
              country: "",
              websiteURL: row.websiteURL || "",
              whatsAppAccessToken: row.whatsAppAccessToken || "",
              whatsAppPhoneNumberId: row.whatsAppPhoneNumberId || "",
              whatsAppNotificationLimit:
                row.whatsAppNotificationLimit != null
                  ? String(row.whatsAppNotificationLimit)
                  : "",
              dataRegion: row.dataRegion || "",
              webhookEnabled: Boolean(row.webhookEnabled),
              isImageCreationAllowed: Boolean(row.isImageCreationAllowed),
              isExternalBatchProcessing: Boolean(
                row.isExternalBatchProcessing,
              ),
              isManualGradingAllowed: Boolean(row.isManualGradingAllowed),
              isOnlineExamAllowed: Boolean(row.isOnlineExamAllowed),
              isAdvancedOnlineExamAllowed: Boolean(
                row.isAdvancedOnlineExamAllowed,
              ),
              hasSubscribed: Boolean(row.hasSubscribed),
              allowStudentClassJoin: Boolean(row.allowStudentClassJoin),
              autoGenerateStudentPassword: Boolean(
                row.autoGenerateStudentPassword,
              ),
              allowHomeworkGrading: Boolean(row.allowHomeworkGrading),
              webhookURL: row.webhookURL || "",
              logo: null,
              logoURL: row.logoURL || "",
              logoChanged: false,
              combinedGradingBlockSize: row.combinedGradingBlockSize || null,
              questionBlockSize: row.questionBlockSize || null,
              isPageRangeRulesEnabled: Boolean(
                row.pageRangeCreditUsageRules &&
                  row.pageRangeCreditUsageRules.length > 0,
              ),
              pageRangeCreditUsageRules: rules,
              internalCOWeightage:
                row.COPOConfig?.internalCOWeightage ??
                defaultCOPOConfig.internalCOWeightage,
              externalCOWeightage:
                row.COPOConfig?.externalCOWeightage ??
                defaultCOPOConfig.externalCOWeightage,
              directCOWeightage:
                row.COPOConfig?.directCOWeightage ??
                defaultCOPOConfig.directCOWeightage,
              indirectCOWeightage:
                row.COPOConfig?.indirectCOWeightage ??
                defaultCOPOConfig.indirectCOWeightage,
              directPOWeightage:
                row.COPOConfig?.directPOWeightage ??
                defaultCOPOConfig.directPOWeightage,
              indirectPOWeightage:
                row.COPOConfig?.indirectPOWeightage ??
                defaultCOPOConfig.indirectPOWeightage,
              coAttainmentMethod:
                row.COPOConfig?.coAttainmentMethod ??
                defaultCOPOConfig.coAttainmentMethod,
              // SCORE_BASED_ATTAINMENT criteria
              level1MinScorePercentage:
                row.COPOConfig?.scoreBasedAttainmentCriteria
                  ?.level1MinScorePercentage ??
                defaultCOPOConfig.scoreBasedAttainment
                  .level1MinScorePercentage,
              level2MinScorePercentage:
                row.COPOConfig?.scoreBasedAttainmentCriteria
                  ?.level2MinScorePercentage ??
                defaultCOPOConfig.scoreBasedAttainment
                  .level2MinScorePercentage,
              level3MinScorePercentage:
                row.COPOConfig?.scoreBasedAttainmentCriteria
                  ?.level3MinScorePercentage ??
                defaultCOPOConfig.scoreBasedAttainment
                  .level3MinScorePercentage,
              scoreAveragingBasis:
                row.COPOConfig?.scoreAveragingBasis ??
                defaultCOPOConfig.scoreAveragingBasis,
              // THRESHOLD_BASED_ATTAINMENT criteria
              minScorePercentage:
                row.COPOConfig?.thresholdBasedAttainmentCriteria
                  ?.minScorePercentage ??
                defaultCOPOConfig.thresholdBasedAttainment
                  .minScorePercentage,
              level1MinStudentPercentage:
                row.COPOConfig?.thresholdBasedAttainmentCriteria
                  ?.level1MinStudentPercentage ??
                defaultCOPOConfig.thresholdBasedAttainment
                  .level1MinStudentPercentage,
              level2MinStudentPercentage:
                row.COPOConfig?.thresholdBasedAttainmentCriteria
                  ?.level2MinStudentPercentage ??
                defaultCOPOConfig.thresholdBasedAttainment
                  .level2MinStudentPercentage,
              level3MinStudentPercentage:
                row.COPOConfig?.thresholdBasedAttainmentCriteria
                  ?.level3MinStudentPercentage ??
                defaultCOPOConfig.thresholdBasedAttainment
                  .level3MinStudentPercentage,
              isCOPOEnabled: Boolean(row.COPOConfig),
              isQRPageCountDetectionEnabled: Boolean(
                row.isQRPageCountDetectionEnabled,
              ),
              isReportCardEnabled: Boolean(row.isReportCardEnabled),
              isCreditReportEnabled: Boolean(row.isCreditReportEnabled),
              feedbackMode: row.feedbackMode || "APP_FEEDBACK_ONLY",
              slaMaxProcessingHours:
                row.sla?.maxProcessingHours != null
                  ? String(row.sla.maxProcessingHours)
                  : "",
              annotationPrintingOffsetConfig: Array.isArray(
                row.annotationPrintingOffsetConfig,
              )
                ? row.annotationPrintingOffsetConfig.map((item) => ({
                    ...(item._id ? { _id: item._id } : {}),
                    name: item.name || "",
                    topOffset:
                      item.topOffset != null ? String(item.topOffset) : "",
                    rightOffset:
                      item.rightOffset != null ? String(item.rightOffset) : "",
                  }))
                : [],
              aiProviderConfig: Array.isArray(row.aiProviderConfig)
                ? row.aiProviderConfig.map((item) => ({
                    feature: item.feature || "",
                    aiModelConfigId: item.aiModelConfigId?._id || item.aiModelConfigId || item.aiModelConfig?._id || item.aiModelConfig || "",
                  }))
                : [],
            };
            setInitialEditFormData(initialFormState);
            setEditFormData(initialFormState);
            setIsEditModalOpen(true);
          }}
        >
          <i className="fa-solid fa-pen-to-square"></i>
        </button>
      ),
    },
    ...(filterType !== "subscribed" || filterType === "expired"
      ? [
          {
            key: "delete",
            label: "Delete",
            // Subscribed non-expired institutes appear with dash (0); deletable ones (expired or non-subscribed) appear with 1
            sortBy: (row) =>
              row.hasSubscribed && !isCreditExpired(row) ? 0 : 1,
            sortable: true,
            render: (row) => {
              // Active subscribed institutes that are not expired cannot be deleted
              if (row.hasSubscribed && !isCreditExpired(row)) {
                return "-"; // show dash if subscribed and not expired
              }

              return (
                <button
                  className="delete-btn"
                  onClick={() => handleDelete(row._id, row.name)}
                  disabled={isDeleting && deletingId === row._id}
                  title={`Delete ${row.name}`}
                >
                  {isDeleting && deletingId === row._id ? (
                    <Loader
                      style={{
                        fontSize: "1em",
                        color: "currentColor",
                        top: "0",
                        left: "0",
                      }}
                    />
                  ) : (
                    <i className="fa-solid fa-trash"></i>
                  )}
                </button>
              );
            },
          },
        ]
      : []),
  ];


  const fetchData = useCallback(
    async (
      currentPage = 1,
      currentSearch = "",
      currentSortBy = sortBy,
      currentSortOrder = sortOrder,
      currentFilter = filterType,
    ) => {
      setLoading(true);
      try {
        const queryParams = new URLSearchParams({
          page: currentPage,
          limit: pageSize,
        });
        if (currentFilter && currentFilter !== "") {
          queryParams.append("filterType", currentFilter);
        }
        if (currentSearch && currentSearch.trim() !== "") {
          queryParams.append("search", currentSearch.trim());
        }
        if (currentSortBy && currentSortBy.trim() !== "") {
          queryParams.append("sortBy", currentSortBy.trim());
          queryParams.append("sortOrder", currentSortOrder || "asc");
        }
        const response = await fetchWithAuth(
          `${serverUrl}/api/institute/get-all?${queryParams.toString()}`,
          {
            method: "GET",
            credentials: "include",
          },
        );
        if (!response) return;
        const resData = await response.json();
        if (!response.ok) {
          swalError(resData.message || "Failed to fetch institutes");
          return;
        }

        const institutesList = Array.isArray(resData)
          ? resData
          : resData.institutes || [];
        const total =
          typeof resData.total === "number"
            ? resData.total
            : institutesList.length;

        setTableData(institutesList);
        setTotalRecords(total);
        if (typeof resData.expiredCount === "number") {
          setExpiredInstitutesCount(resData.expiredCount);
        }
        setError(null);

        // Fetch usage data only for subscribed institutes on this page
        const subscribedInstitutes = institutesList.filter(
          (inst) => inst.hasSubscribed,
        );
        if (subscribedInstitutes.length > 0) {
          const usagePromises = subscribedInstitutes.map((inst) =>
            fetchWithAuth(`${serverUrl}/api/institute/get-usage/${inst._id}`, {
              credentials: "include",
            }).then((res) => (res.ok ? res.json() : null)),
          );
          const usages = await Promise.all(usagePromises);
          const newUsageMap = {};
          subscribedInstitutes.forEach((inst, i) => {
            if (usages[i]) {
              newUsageMap[inst._id] = usages[i];
            }
          });
          setUsageMap((prev) => ({ ...prev, ...newUsageMap }));
        }
      } catch (err) {
        setError(err.message);
        swalError(err.message || "Failed to fetch institutes");
      } finally {
        setLoading(false);
      }
    },
    [pageSize, sortBy, sortOrder, filterType],
  );

  const handleSortChange = (key, direction) => {
    const order = direction === "descending" || direction === "desc" ? "desc" : "asc";
    setSortBy(key);
    setSortOrder(order);
    setPage(1);
  };

  useEffect(() => {
    fetchData(page, searchTerm, sortBy, sortOrder, filterType);
  }, [page, searchTerm, sortBy, sortOrder, pageSize, filterType, fetchData]);

  useEffect(() => {
    fetchLOVs();
  }, []);

  const fetchLOVs = async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/lov/get-all-by-type/ai-model`,
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch LOVs");
      const data = await response.json();

      const fetchedAiModels = data.map((aiModel) => ({
        value: aiModel.value,
        label: aiModel.value,
      }));

      setAIModels((prev) => {
        const combined = [...prev];
        fetchedAiModels.forEach((f) => {
          if (!combined.some((m) => m.value.toLowerCase() === f.value.toLowerCase())) {
            combined.push(f);
          }
        });
        return combined;
      });

      const response1 = await fetchWithAuth(
        `${serverUrl}/api/lov/get-all-by-type/ai-modelUseType`,
      );
      if (!response1) return;
      if (!response1.ok) throw new Error("Failed to fetch LOVs");
      const data1 = await response1.json();

      const aiModelUseTypes = data1.map((aiModelUseType) => ({
        value: aiModelUseType.value,
        label: aiModelUseType.value,
      }));

      setAIModelUseTypes(aiModelUseTypes);

      const categoryResponse = await fetchWithAuth(
        `${serverUrl}/api/lov/get-all-by-type/category`,
      );
      if (!categoryResponse) return;
      if (!categoryResponse.ok)
        throw new Error("Failed to fetch category LOVs");
      const categoryLOVs = await categoryResponse.json();

      const categories = categoryLOVs.map((category) => ({
        value: category.value,
        label: category.value?.replace(/_/g, " "),
      }));

      setCategoryOptions(categories);

      const templatesResponse = await fetchWithAuth(
        `${serverUrl}/api/standardized-test/get-all`,
      );
      if (templatesResponse && templatesResponse.ok) {
        const templatesData = await templatesResponse.json();
        const dataToMap = Array.isArray(templatesData) ? templatesData : (templatesData.data || []);
        const templatesOptions = dataToMap.map((t) => ({
          value: t._id,
          label: t.templateName
            ? `${t.templateName} (${t.templateId})`
            : t.templateId,
        }));
        setStandardizedTemplatesOptions(templatesOptions);
      }

      const curriculumResponse = await fetchWithAuth(
        `${serverUrl}/api/lov/get-all-by-type/curriculum`,
      );
      if (!curriculumResponse) return;
      if (!curriculumResponse.ok) throw new Error("Failed to fetch LOVs");
      const curriculumLOVs = await curriculumResponse.json();

      const curriculums = curriculumLOVs.map((curriculum) => ({
        value: curriculum.value,
        label: curriculum.value,
      }));

      setCurriculumOptions(curriculums);

      const coAttainmentMethodResponse = await fetchWithAuth(
        `${serverUrl}/api/lov/get-all-by-type/coAttainmentMethod`,
      );
      if (!coAttainmentMethodResponse) return;
      if (!coAttainmentMethodResponse.ok)
        throw new Error("Failed to fetch LOVs");
      const coAttainmentMethodLOVs = await coAttainmentMethodResponse.json();

      const coAttainmentMethods = coAttainmentMethodLOVs.map((method) => ({
        value: method.value,
        label: method.value,
      }));

      setCoAttainmentMethodOptions(coAttainmentMethods);
      setCoAttainmentMethodOptions(coAttainmentMethods);

      const scoreAveragingBasisResponse = await fetchWithAuth(
        `${serverUrl}/api/lov/get-all-by-type/scoreAveragingBasis`,
      );
      if (scoreAveragingBasisResponse && scoreAveragingBasisResponse.ok) {
        const scoreAveragingBasisLOVs =
          await scoreAveragingBasisResponse.json();
        const scoreAveragingBasisOpts = scoreAveragingBasisLOVs.map(
          (basis) => ({
            value: basis.value,
            label: basis.value,
          }),
        );
        setScoreAveragingBasisOptions(scoreAveragingBasisOpts);
      }

      const reportCardTemplateResponse = await fetchWithAuth(
        `${serverUrl}/api/lov/get-all-by-type/reportCardTemplate`,
      );
      if (reportCardTemplateResponse && reportCardTemplateResponse.ok) {
        const reportCardTemplateLOVs =
          await reportCardTemplateResponse.json();
        const reportCardTemplates = reportCardTemplateLOVs
          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
          .map((template) => ({
            value: template.value,
            label: template.displayValue || template.value,
          }));
        setReportCardTemplateOptions(reportCardTemplates);
      }
    } catch (err) {
      swalError(err.message);
    }
  };


  // Helper function for usage display (similar to UsageCard logic)
  const getUsageDisplay = (usageData, hasSubscribed) => {
    if (!hasSubscribed) return { text: "N/A", className: "usage-na" };
    if (!usageData) return { text: "-", className: "" };
    const used = usageData.usageSize || 0;
    const total = usageData.purchasedSize || 0;
    const percentage = total > 0 ? (used / total) * 100 : 0;
    let status = "normal";
    if (used > total) status = "over";
    else if (percentage >= 90) status = "critical";
    else if (percentage >= 75) status = "warning";
    const text = `${used} / ${total}`;
    return { text, className: `usage-${status}` };
  };

  const handleExportToExcel = async (customData = null) => {
    try {
      let exportList = [];
      if (customData && Array.isArray(customData)) {
        exportList = customData;
      } else {
        setIsExportingExcel(true);
        const params = new URLSearchParams({
          all: "true",
          filterType: filterType,
        });
        if (searchTerm && searchTerm.trim() !== "") {
          params.append("search", searchTerm.trim());
        }
        if (sortBy && sortBy.trim() !== "") {
          params.append("sortBy", sortBy.trim());
          params.append("sortOrder", sortOrder || "asc");
        }
        const resp = await fetchWithAuth(
          `${serverUrl}/api/institute/get-all?${params.toString()}`,
          { method: "GET", credentials: "include" },
        );
        if (!resp || !resp.ok) {
          throw new Error("Failed to fetch institutes for export");
        }
        const resData = await resp.json();
        exportList = Array.isArray(resData)
          ? resData
          : (resData.institutes || []);
      }

      if (!exportList || exportList.length === 0) {
        swalWarning("No institutes available to export.");
        return;
      }

      setIsExportingExcel(true);

      // Fetch usage data for any subscribed institutes missing from usageMap
      const subscribedWithoutUsage = exportList.filter(
        (inst) => inst.hasSubscribed && !usageMap[inst._id],
      );
      if (subscribedWithoutUsage.length > 0) {
        const usagePromises = subscribedWithoutUsage.map((inst) =>
          fetchWithAuth(`${serverUrl}/api/institute/get-usage/${inst._id}`, {
            credentials: "include",
          }).then((res) => (res.ok ? res.json() : null)),
        );
        const usages = await Promise.all(usagePromises);
        const newUsage = {};
        subscribedWithoutUsage.forEach((inst, i) => {
          if (usages[i]) {
            newUsage[inst._id] = usages[i];
          }
        });
        setUsageMap((prev) => ({ ...prev, ...newUsage }));
      }

      const workbook = new ExcelJS.Workbook();
      workbook.creator = "GradeMe AI";
      workbook.lastModifiedBy = "GradeMe AI Super Admin";
      workbook.created = new Date();
      workbook.modified = new Date();

      let sheetName = "Institutes";
      let filePrefix = "Institutes";

      if (customData && Array.isArray(customData)) {
        sheetName = "Selected Institutes";
        filePrefix = "Selected_Institutes";
      } else if (filterType === "expired") {
        sheetName = "Expired Institutes";
        filePrefix = "Expired_Institutes";
      } else if (filterType === "subscribed") {
        sheetName = "Subscribed Institutes";
        filePrefix = "Subscribed_Institutes";
      } else if (filterType === "non-subscribed") {
        sheetName = "Non-Subscribed Institutes";
        filePrefix = "Non_Subscribed_Institutes";
      } else {
        sheetName = "All Institutes";
        filePrefix = "All_Institutes";
      }

      const worksheet = workbook.addWorksheet(sheetName, {
        views: [{ showGridLines: true }],
      });

      // Columns configuration
      worksheet.columns = [
        { header: "S.No.", key: "sNo", width: 8 },
        { header: "Institute Code", key: "code", width: 18 },
        { header: "Institute Name", key: "name", width: 34 },
        { header: "Tagline", key: "tagline", width: 28 },
        { header: "Address", key: "address", width: 32 },
        { header: "Domain", key: "domain", width: 22 },
        { header: "Category", key: "category", width: 20 },
        { header: "Subscription Status", key: "subscriptionStatus", width: 20 },
        { header: "Grading Credit Usage", key: "gradingCreditUsage", width: 22 },
        { header: "Grading Used", key: "gradingUsed", width: 15 },
        { header: "Grading Total", key: "gradingTotal", width: 15 },
        { header: "QB Credit Usage", key: "qbCreditUsage", width: 22 },
        { header: "QB Used", key: "qbUsed", width: 15 },
        { header: "QB Total", key: "qbTotal", width: 15 },
        { header: "Credit Expiry Date", key: "expiryDate", width: 20 },
        { header: "Credit Status", key: "creditStatus", width: 16 },
        { header: "Contact Number", key: "phoneNumber", width: 18 },
        { header: "Country", key: "country", width: 16 },
        { header: "Website", key: "websiteURL", width: 28 },
      ];

      // Style header row
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true, color: { argb: "FFFFFF" }, size: 11 };
      headerRow.alignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };
      headerRow.height = 30;

      const headerColor = filterType === "expired" ? "DC2626" : "1E3A8A";
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: headerColor },
        };
        cell.border = {
          top: { style: "thin", color: { argb: "CBD5E1" } },
          left: { style: "thin", color: { argb: "CBD5E1" } },
          bottom: { style: "thin", color: { argb: "CBD5E1" } },
          right: { style: "thin", color: { argb: "CBD5E1" } },
        };
      });

      // Populate data rows
      exportList.forEach((inst, index) => {
        const gradingUsage = usageMap[inst._id]?.combinedGrading;
        const qbUsage = usageMap[inst._id]?.questionBlock;

        const gradingDisplay = getUsageDisplay(
          gradingUsage,
          inst.hasSubscribed,
        );
        const qbDisplay = getUsageDisplay(qbUsage, inst.hasSubscribed);

        const expired = isCreditExpired(inst);
        const creditStatusText = inst.gradingCreditExpiryDate
          ? expired
            ? "Expired"
            : "Active"
          : "No Expiry";

        const row = worksheet.addRow({
          sNo: index + 1,
          code: inst.code || "-",
          name: inst.name || "-",
          tagline: inst.tagline || "-",
          address: inst.address || "-",
          domain: inst.domain || "-",
          category: inst.category || "-",
          subscriptionStatus: inst.hasSubscribed
            ? "Subscribed"
            : "Non-Subscribed",
          gradingCreditUsage: gradingDisplay.text,
          gradingUsed: inst.hasSubscribed
            ? (gradingUsage?.usageSize ?? 0)
            : "N/A",
          gradingTotal: inst.hasSubscribed
            ? (gradingUsage?.purchasedSize ?? 0)
            : "N/A",
          qbCreditUsage: qbDisplay.text,
          qbUsed: inst.hasSubscribed ? (qbUsage?.usageSize ?? 0) : "N/A",
          qbTotal: inst.hasSubscribed
            ? (qbUsage?.purchasedSize ?? 0)
            : "N/A",
          expiryDate: inst.gradingCreditExpiryDate
            ? displayDateOnly(inst.gradingCreditExpiryDate)
            : "No Expiry",
          creditStatus: creditStatusText,
          phoneNumber:
            inst.formattedPhoneNumber || inst.phoneNumber || "-",
          country: inst.country || "-",
          websiteURL: inst.websiteURL || "-",
        });

        row.height = 22;
        row.alignment = { vertical: "middle" };

        row.getCell("sNo").alignment = {
          vertical: "middle",
          horizontal: "center",
        };
        row.getCell("code").alignment = {
          vertical: "middle",
          horizontal: "center",
        };
        row.getCell("subscriptionStatus").alignment = {
          vertical: "middle",
          horizontal: "center",
        };
        row.getCell("gradingCreditUsage").alignment = {
          vertical: "middle",
          horizontal: "center",
        };
        row.getCell("gradingUsed").alignment = {
          vertical: "middle",
          horizontal: "center",
        };
        row.getCell("gradingTotal").alignment = {
          vertical: "middle",
          horizontal: "center",
        };
        row.getCell("qbCreditUsage").alignment = {
          vertical: "middle",
          horizontal: "center",
        };
        row.getCell("qbUsed").alignment = {
          vertical: "middle",
          horizontal: "center",
        };
        row.getCell("qbTotal").alignment = {
          vertical: "middle",
          horizontal: "center",
        };
        row.getCell("expiryDate").alignment = {
          vertical: "middle",
          horizontal: "center",
        };
        row.getCell("creditStatus").alignment = {
          vertical: "middle",
          horizontal: "center",
        };

        if (expired) {
          row.getCell("creditStatus").font = {
            color: { argb: "DC2626" },
            bold: true,
          };
          row.getCell("expiryDate").font = {
            color: { argb: "DC2626" },
            bold: true,
          };
        }

        if (index % 2 === 1) {
          row.eachCell({ includeEmpty: true }, (cell) => {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "F8FAFC" },
            };
          });
        }

        row.eachCell({ includeEmpty: true }, (cell) => {
          cell.border = {
            top: { style: "thin", color: { argb: "E2E8F0" } },
            left: { style: "thin", color: { argb: "E2E8F0" } },
            bottom: { style: "thin", color: { argb: "E2E8F0" } },
            right: { style: "thin", color: { argb: "E2E8F0" } },
          };
        });
      });

      const now = new Date();
      const dateStr = `${String(now.getDate()).padStart(2, "0")}-${String(
        now.getMonth() + 1,
      ).padStart(2, "0")}-${now.getFullYear()}`;
      const filename = `${filePrefix}_${dateStr}.xlsx`;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, filename);

      swalSuccess(
        `Excel exported successfully! (${exportList.length} institute${
          exportList.length === 1 ? "" : "s"
        })`,
      );
    } catch (err) {
      console.error("Failed to export institutes to Excel:", err);
      swalError("Failed to export Excel file. Please try again.");
    } finally {
      setIsExportingExcel(false);
    }
  };

  document.title = "GradeMe-AI: Manage Institutes";

  if (!isAuthenticated || primaryRole !== "superadmin") {
    return <Navigate to="/login" replace />;
  }

  if (error) {
    return <div className="error-div">{error}</div>;
  }

  const handleInputChange = (e) => {
    const { name, type, value, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleSelectChange = (selectedOption, { name }) => {
    const val = selectedOption ? selectedOption.value : "";
    setFormData((prev) => ({
      ...prev,
      [name]: val,
    }));
  };

  const handleRemoveLogo = () => {
    setEditFormData((prev) => ({
      ...prev,
      logo: null,
      logoURL: "",
      logoChanged: true,
    }));
  };

  const handleEditInputChange = (e) => {
    const { name, type, value, checked, files } = e.target;
    if (name === "logo") {
      const file = files && files[0];
      setEditFormData((prev) => ({
        ...prev,
        logo: file || null,
        logoChanged: true,
      }));
    } else if (name === "isPageRangeRulesEnabled") {
      setEditFormData((prev) => ({
        ...prev,
        isPageRangeRulesEnabled: checked,
        pageRangeCreditUsageRules: checked
          ? originalPageRangeRules && originalPageRangeRules.length > 0
            ? originalPageRangeRules
            : [{ minPageCount: 1, maxPageCount: "", creditUsage: "" }]
          : [],
      }));
    } else {
      const newValue = type === "checkbox" ? checked : value;
      setEditFormData((prev) => ({
        ...prev,
        [name]: newValue,
      }));
    }
  };

  const handleEditSelectChange = (selectedOption, { name }) => {
    const val = selectedOption ? selectedOption.value : "";
    setEditFormData((prev) => ({
      ...prev,
      [name]: val,
    }));
  };

  const addPageRangeEntry = (isEdit = false) => {
    const data = isEdit ? editFormData : formData;
    const lastEntry =
      data.pageRangeCreditUsageRules[data.pageRangeCreditUsageRules.length - 1];
    const newMinPage = lastEntry?.maxPageCount
      ? parseInt(lastEntry.maxPageCount) + 1
      : 1;
    const newEntry = {
      minPageCount: newMinPage,
      maxPageCount: "",
      creditUsage: "",
    };
    if (isEdit) {
      setEditFormData((prev) => ({
        ...prev,
        pageRangeCreditUsageRules: [
          ...prev.pageRangeCreditUsageRules,
          newEntry,
        ],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        pageRangeCreditUsageRules: [
          ...prev.pageRangeCreditUsageRules,
          newEntry,
        ],
      }));
    }
  };

  const removePageRangeEntry = (index, isEdit = false) => {
    if (index === 0) return; // Cannot remove first entry
    if (isEdit) {
      setEditFormData((prev) => ({
        ...prev,
        pageRangeCreditUsageRules: prev.pageRangeCreditUsageRules.filter(
          (_, i) => i !== index,
        ),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        pageRangeCreditUsageRules: prev.pageRangeCreditUsageRules.filter(
          (_, i) => i !== index,
        ),
      }));
    }
  };

  const addOffsetConfigEntry = (isEdit = false) => {
    const newEntry = {
      name: "",
      topOffset: "",
      rightOffset: "",
    };
    if (isEdit) {
      setEditFormData((prev) => ({
        ...prev,
        annotationPrintingOffsetConfig: [
          ...(prev.annotationPrintingOffsetConfig || []),
          newEntry,
        ],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        annotationPrintingOffsetConfig: [
          ...(prev.annotationPrintingOffsetConfig || []),
          newEntry,
        ],
      }));
    }
  };

  const removeOffsetConfigEntry = (index, isEdit = false) => {
    if (isEdit) {
      setEditFormData((prev) => ({
        ...prev,
        annotationPrintingOffsetConfig: (
          prev.annotationPrintingOffsetConfig || []
        ).filter((_, i) => i !== index),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        annotationPrintingOffsetConfig: (
          prev.annotationPrintingOffsetConfig || []
        ).filter((_, i) => i !== index),
      }));
    }
  };

  const updateOffsetConfigEntry = (index, field, value, isEdit = false) => {
    const updater = (prev) => {
      const updated = [...(prev.annotationPrintingOffsetConfig || [])];
      if (updated[index]) {
        updated[index] = {
          ...updated[index],
          [field]: value,
        };
      }
      return {
        ...prev,
        annotationPrintingOffsetConfig: updated,
      };
    };
    if (isEdit) {
      setEditFormData(updater);
    } else {
      setFormData(updater);
    }
  };

  const addAIProviderConfigEntry = (isEdit = false) => {
    const newEntry = {
      feature: "",
      aiModelConfigId: "",
    };
    if (isEdit) {
      setEditFormData((prev) => ({
        ...prev,
        aiProviderConfig: [...(prev.aiProviderConfig || []), newEntry],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        aiProviderConfig: [...(prev.aiProviderConfig || []), newEntry],
      }));
    }
  };

  const removeAIProviderConfigEntry = (index, isEdit = false) => {
    if (isEdit) {
      setEditFormData((prev) => ({
        ...prev,
        aiProviderConfig: (prev.aiProviderConfig || []).filter((_, i) => i !== index),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        aiProviderConfig: (prev.aiProviderConfig || []).filter((_, i) => i !== index),
      }));
    }
  };

  const updateAIProviderConfigEntry = (index, field, value, isEdit = false) => {
    const currentList = isEdit
      ? editFormData.aiProviderConfig || []
      : formData.aiProviderConfig || [];

    if (field === "feature") {
      const nextFeature = value;
      if (nextFeature) {
        const isDuplicate = currentList.some(
          (item, i) => i !== index && item.feature === nextFeature
        );
        if (isDuplicate) {
          swalWarning("Duplicate Feature", "Feature '" + nextFeature + "' is already configured for this institute.");
          return;
        }
      }

      const matchingConfigs = (aiFeatureOptions.configs || []).filter(
        (c) => c.type === nextFeature || (['HomeWorkGrader', 'UPSCGrader', 'LanguageGrader'].includes(nextFeature) && c.type === 'Grader')
      );
      const { gemini, openai, other } = groupConfigsByProvider(matchingConfigs);
      const sortedMatchingConfigs = [...gemini, ...openai, ...other];
      const nextAiModelConfigId = sortedMatchingConfigs[0]?._id || "";

      const updater = (prev) => {
        const updated = [...(prev.aiProviderConfig || [])];
        if (updated[index]) {
          updated[index] = {
            feature: nextFeature,
            aiModelConfigId: nextAiModelConfigId,
          };
        }
        return { ...prev, aiProviderConfig: updated };
      };

      if (isEdit) setEditFormData(updater);
      else setFormData(updater);
      return;
    }

    if (field === "aiModelConfigId") {
      const updater = (prev) => {
        const updated = [...(prev.aiProviderConfig || [])];
        if (updated[index]) {
          updated[index] = {
            ...updated[index],
            aiModelConfigId: value,
          };
        }
        return { ...prev, aiProviderConfig: updated };
      };

      if (isEdit) setEditFormData(updater);
      else setFormData(updater);
      return;
    }
  };

  const updatePageRangeEntry = (index, field, value, isEdit = false) => {
    if (isEdit) {
      setEditFormData((prev) => ({
        ...prev,
        pageRangeCreditUsageRules: prev.pageRangeCreditUsageRules.map(
          (entry, i) => (i === index ? { ...entry, [field]: value } : entry),
        ),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        pageRangeCreditUsageRules: prev.pageRangeCreditUsageRules.map(
          (entry, i) => (i === index ? { ...entry, [field]: value } : entry),
        ),
      }));
    }
  };

  return (
    <div className="super-admin-container">
      <Navbar
        createButtons={[
          {
            text: "Create Institute",
            onClick: () => setIsCreateModalOpen(true),
          },
        ]}
        pageName="Manage Institutes"
        breadcrumbs={breadcrumbs}
      />
      <div className="quick-actions-bar">
        <div className="quick-actions-right">
          <div className="filters-group" style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
            {/* Filter Dropdown */}
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <label
                htmlFor="institute-filter-select"
                style={{
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  color: "#4b5563",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <i className="fa-solid fa-filter" style={{ color: "#4f46e5" }}></i>
                Filter:
              </label>
              <select
                id="institute-filter-select"
                className="form-select"
                value={filterType}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedInstituteIds(new Set());
                  setSelectedInstitutesMap(new Map());
                  setFilterType(val);
                  setPage(1);
                }}
                style={{
                  padding: "5px 10px",
                  fontSize: "0.85rem",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  background: "#ffffff",
                  color: filterType === "expired" ? "#dc2626" : "#111827",
                  fontWeight: filterType === "expired" ? 600 : 500,
                  cursor: "pointer",
                }}
              >
                <option value="subscribed">Subscribed Only</option>
                <option value="all">All Institutes</option>
                <option value="non-subscribed">Non-Subscribed Only</option>
                <option value="expired">
                  Expired Only ({expiredInstitutesCount})
                </option>
              </select>
            </div>

            {/* Download Excel Button for Active Filter */}
            <button
              type="button"
              className="export-excel-btn"
              onClick={() => handleExportToExcel()}
              disabled={isExportingExcel || totalRecords === 0}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "7px",
                padding: "6px 14px",
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                color: "#ffffff",
                border: "none",
                borderRadius: "6px",
                fontWeight: 600,
                fontSize: "0.85rem",
                cursor:
                  isExportingExcel || totalRecords === 0
                    ? "not-allowed"
                    : "pointer",
                boxShadow: "0 2px 6px rgba(16, 185, 129, 0.25)",
                opacity:
                  isExportingExcel || totalRecords === 0 ? 0.6 : 1,
                transition: "all 0.2s ease",
              }}
              title={`Download Excel sheet of ${
                filterType === "expired"
                  ? "Expired Only"
                  : filterType === "subscribed"
                  ? "Subscribed Only"
                  : filterType === "non-subscribed"
                  ? "Non-Subscribed Only"
                  : "All"
              } institutes (${totalRecords})`}
            >
              <i
                className={
                  isExportingExcel
                    ? "fa-solid fa-spinner fa-spin"
                    : "fa-solid fa-file-excel"
                }
                style={{ fontSize: "0.95rem" }}
              ></i>
              <span>
                {isExportingExcel
                  ? "Exporting..."
                  : `Download Excel (${totalRecords})`}
              </span>
            </button>
          </div>
        </div>
      </div>
      <div className="table-section" style={{ marginTop: "0" }}>
        {loading ? (
          <Loader style={{ fontSize: "2.2rem" }} />
        ) : (
          <Table
            data={tableData}
            columns={columns}
            caption={
              filterType === "expired"
                ? `Institutes (Credit Expired: ${totalRecords})`
                : filterType === "subscribed"
                ? "Institutes (Subscribed)"
                : "Institutes"
            }
            textAlign="center"
            serverSide={true}
            totalRecords={totalRecords}
            page={page}
            pageSize={pageSize}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(1);
            }}
            sortBy={sortBy}
            sortOrder={sortOrder === "asc" ? "ascending" : "descending"}
            onPageChange={(newPage) => setPage(newPage)}
            onSortChange={handleSortChange}
            onSearchChange={(searchVal) => {
              setSearchTerm(searchVal);
              setPage(1);
            }}
            style={{ marginTop: "0" }}
            isCheckBoxAvailable={filterType === "expired"}
            selectedRowIds={selectedInstituteIds}
            onRowSelect={handleRowSelect}
            onSelectAll={handleSelectAll}
            disableRowClickSelection={true}
            searchActions={
              selectedInstituteIds.size > 0 ? (
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <button
                    type="button"
                    onClick={() => handleExportToExcel(selectedInstitutesList)}
                    disabled={isExportingExcel}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 12px",
                      borderRadius: "6px",
                      background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      color: "#fff",
                      border: "none",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      cursor: isExportingExcel ? "not-allowed" : "pointer",
                      boxShadow: "0 2px 6px rgba(16, 185, 129, 0.25)",
                    }}
                    title="Download Excel for selected institutes only"
                  >
                    <i className="fa-solid fa-file-excel"></i>
                    <span>Export Selected ({selectedInstituteIds.size})</span>
                  </button>
                  <button
                    type="button"
                    className="delete-btn"
                    onClick={handleOpenBulkDelete}
                    disabled={isBulkDeleting}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 12px",
                      borderRadius: "6px",
                      background: "#dc2626",
                      color: "#fff",
                      border: "none",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      cursor: "pointer",
                      boxShadow: "0 2px 6px rgba(220, 38, 38, 0.25)",
                    }}
                    title="Delete selected institutes"
                  >
                    <i className="fa-solid fa-trash"></i>
                    <span>Delete ({selectedInstituteIds.size})</span>
                  </button>
                  {selectedInstituteIds.size > 0 && (
                    <button
                      type="button"
                      className="quick-action-btn"
                      onClick={() => {
                        setSelectedInstituteIds(new Set());
                        setSelectedInstitutesMap(new Map());
                      }}
                      disabled={isBulkDeleting}
                      title="Clear all selections"
                      style={{
                        fontSize: "0.82rem",
                        color: "#6b7280",
                      }}
                    >
                      <i className="fa-solid fa-xmark"></i>
                      <span>Clear Selection</span>
                    </button>
                  )}
                </div>
              ) : null
            }
          />
        )}
      </div>

      {/* Create AI Model Config */}
      <Modal
        isOpen={isCreateAIConfigModalOpen}
        onClose={() => !isSubmitting && setIsCreateAIConfigModalOpen(false)}
      >
        <h2>Create New AI Model Config</h2>
        <form onSubmit={handleCreateAIModelConfig}>
          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Provider
            </label>
            <Select
              id="aiConfigProvider"
              name="Provider"
              options={DEFAULT_AI_PROVIDERS}
              value={
                DEFAULT_AI_PROVIDERS.find(
                  (p) => p.value === (aiProvider || "gemini"),
                ) || { value: aiProvider, label: aiProvider }
              }
              onChange={(opt) => setAiProvider(opt?.value || "gemini")}
              placeholder="Select Provider..."
              isClearable={false}
              required
            />
          </div>

          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> AI Model Use
            </label>
            <Select
              id="modelUseType"
              name="AI Model Use"
              options={aiModelUseTypes}
              value={
                aiModelUseTypes.find(
                  (option) => option.value === aiModelUseType,
                ) || null
              }
              onChange={(selectedOption) =>
                setAiModelUseType(selectedOption?.value)
              }
              placeholder="Select AI Model Use ..."
              isClearable
              required
            />
          </div>

          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> AI Model
            </label>
            <Select
              id="model"
              name="AIModel"
              options={aiModels}
              value={
                aiModels.find((option) => option.value === aiModel) ||
                (aiModel ? { value: aiModel, label: aiModel } : null)
              }
              onChange={(selectedOption) => {
                const modelValue = selectedOption?.value || null;
                setAiModel(modelValue);
                if (modelValue) {
                  const preset = getGeminiModelConfig(modelValue);
                  if (preset) {
                    setFormData((prev) => ({
                      ...prev,
                      inputCostPerMillionTokens:
                        preset.inputCostPerMillionTokens !== undefined
                          ? preset.inputCostPerMillionTokens
                          : prev.inputCostPerMillionTokens,
                      outputCostPerMillionTokens:
                        preset.outputCostPerMillionTokens !== undefined
                          ? preset.outputCostPerMillionTokens
                          : prev.outputCostPerMillionTokens,
                      thinkingCostPerMillionTokens:
                        preset.thinkingCostPerMillionTokens !== undefined
                          ? preset.thinkingCostPerMillionTokens
                          : prev.thinkingCostPerMillionTokens,
                    }));
                  }
                }
              }}
              placeholder="Select AI Model..."
              isClearable
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="inputCostPerMillionTokens">Input Cost/ M</label>
            <input
              type="text"
              id="inputCostPerMillionTokens"
              name="inputCostPerMillionTokens"
              value={formData.inputCostPerMillionTokens}
              onChange={handleInputChange}
              placeholder="e.g. 0.1"
            />
          </div>
          <div className="form-group">
            <label htmlFor="outputCostPerMillionTokens">Output Cost/ M</label>
            <input
              type="text"
              id="outputCostPerMillionTokens"
              name="outputCostPerMillionTokens"
              value={formData.outputCostPerMillionTokens}
              onChange={handleInputChange}
              placeholder="e.g. 0.1"
            />
          </div>
          <div className="form-group">
            <label htmlFor="thinkingCostPerMillionTokens">
              Thinking Cost/ M
            </label>
            <input
              type="text"
              id="thinkingCostPerMillionTokens"
              name="thinkingCostPerMillionTokens"
              value={formData.thinkingCostPerMillionTokens}
              onChange={handleInputChange}
              placeholder="e.g. 0.1"
            />
          </div>

          <div className="form-group">
            <label htmlFor="temperature">Temperature</label>
            <input
              type="text"
              id="temperature"
              name="temperature"
              value={formData.temperature}
              onChange={handleInputChange}
              placeholder="e.g. 0.7"
            />
          </div>
          <div className="form-group">
            <label htmlFor="topK">TopK</label>
            <input
              type="text"
              id="topK"
              name="topK"
              value={formData.topK}
              onChange={handleInputChange}
              placeholder="e.g. 40"
            />
          </div>
          <div className="form-group">
            <label htmlFor="topP">TopP</label>
            <input
              type="text"
              id="topP"
              name="topP"
              value={formData.topP}
              onChange={handleInputChange}
              placeholder="e.g. 0.9"
            />
          </div>
          <div className="form-group">
            <label htmlFor="frequencyPenalty">Frequency Penalty</label>
            <input
              type="text"
              id="frequencyPenalty"
              name="frequencyPenalty"
              value={formData.frequencyPenalty}
              onChange={handleInputChange}
              placeholder="e.g. 0"
            />
          </div>
          <div className="form-group">
            <label htmlFor="presencePenalty">Presence Penalty</label>
            <input
              type="text"
              id="presencePenalty"
              name="presencePenalty"
              value={formData.presencePenalty}
              onChange={handleInputChange}
              placeholder="e.g. 0"
            />
          </div>
          {formError && <div className="error-message">{formError}</div>}
          <button type="submit" className="btn-submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <Loader
                style={{ fontSize: "1em", color: "white", top: "0", left: "0" }}
              />
            ) : (
              <>
                <i className="fa-solid fa-plus"></i> Create AI Model
                Configuration
              </>
            )}
          </button>
        </form>
      </Modal>

      {/* Create Institute Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => !isSubmitting && setIsCreateModalOpen(false)}
        maxWidth="1000px"
      >
        <h2>Create New Institute</h2>
        <form onSubmit={handleCreateInstitute} encType="multipart/form-data">
          <div className="form-group">
            <label htmlFor="name">
              <i className="fa-solid fa-asterisk requiredIcon"></i> Institute
              Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder="Enter institute name"
            />
          </div>
          <div className="form-group">
            <label htmlFor="tagline">Tagline</label>
            <input
              type="text"
              id="tagline"
              name="tagline"
              value={formData.tagline}
              onChange={handleInputChange}
              placeholder="Enter institute tagline"
            />
          </div>
          <div className="form-group">
            <label htmlFor="address">Address</label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="Enter institute address"
            />
          </div>
          <div className="form-group">
            <label htmlFor="code">
              <i className="fa-solid fa-asterisk requiredIcon"></i> Institute
              Code
            </label>
            <input
              type="text"
              id="code"
              name="code"
              value={formData.code}
              onChange={handleInputChange}
              required
              placeholder="Enter institute code"
            />
          </div>

          <div className="form-group">
            <label htmlFor="category"><i className="fa-solid fa-asterisk requiredIcon"></i> Category</label>
            <Select
              name="category"
              options={categoryOptions}
              value={categoryOptions.find(
                (option) => option.value === formData.category,
              )}
              onChange={handleSelectChange}
              placeholder="Select category..."
              required
              isClearable
            />
          </div>

          <div className="form-group">
            <label>Standardized Test Templates</label>
            <Select
              isMulti
              name="standardizedTestTemplateIds"
              options={standardizedTemplatesOptions}
              value={formData.standardizedTestTemplateIds
                ?.map((id) =>
                  standardizedTemplatesOptions.find(
                    (option) => option.value === id,
                  ),
                )
                .filter(Boolean)}
              onChange={(selectedOptions) => {
                setFormData((prev) => ({
                  ...prev,
                  standardizedTestTemplateIds: selectedOptions
                    ? selectedOptions.map((o) => o.value)
                    : [],
                }));
              }}
              placeholder="Select templates..."
              isClearable
            />
          </div>

          {/* Page Range Credit Usage Rules Enabled Toggle Switch */}
          <div className="form-group">
            <label>Page Range Credit Usage Rules Enabled</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="isPageRangeRulesEnabled"
                checked={formData.isPageRangeRulesEnabled}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    isPageRangeRulesEnabled: e.target.checked,
                  }))
                }
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {formData.isPageRangeRulesEnabled ? "Yes" : "No"}
              </span>
            </label>
          </div>

          {/* Page Range Credit Usage Rules */}
          {formData.isPageRangeRulesEnabled && (
            <div className="form-group">
              <label>Page Range Credit Usage Rules</label>
              <ModalTableInput
                headers={[
                  {
                    key: "minPageCount",
                    label: "Min Page Count",
                    width: "1fr",
                    required: true,
                  },
                  {
                    key: "maxPageCount",
                    label: "Max Page Count",
                    width: "1fr",
                    required: true,
                  },
                  {
                    key: "creditUsage",
                    label: "Credit Usage",
                    width: "1fr",
                    required: true,
                  },
                  {
                    key: "actions",
                    label: "Actions",
                    width: "0.8fr",
                  },
                ]}
                rows={formData.pageRangeCreditUsageRules}
                onRowChange={updatePageRangeEntry}
                onRowRemove={(index) => removePageRangeEntry(index, false)}
                onRowAdd={() => addPageRangeEntry(false)}
                canRemove={true}
                addButtonText="Add Entry"
                renderCell={(header, row, rowIndex) => {
                  switch (header.key) {
                    case "minPageCount":
                      return (
                        <input
                          type="number"
                          value={row.minPageCount}
                          disabled={rowIndex === 0}
                          onChange={(e) =>
                            updatePageRangeEntry(
                              rowIndex,
                              "minPageCount",
                              e.target.value,
                              false,
                            )
                          }
                          min="1"
                          step="1"
                          className="modal-table-input"
                          style={{
                            opacity: rowIndex === 0 ? 0.6 : 1,
                            cursor: rowIndex === 0 ? "not-allowed" : "text",
                          }}
                        />
                      );

                    case "maxPageCount":
                      return (
                        <input
                          type="number"
                          value={row.maxPageCount}
                          onChange={(e) =>
                            updatePageRangeEntry(
                              rowIndex,
                              "maxPageCount",
                              e.target.value,
                              false,
                            )
                          }
                          min={row.minPageCount}
                          step="1"
                          className="modal-table-input"
                        />
                      );

                    case "creditUsage":
                      return (
                        <input
                          type="number"
                          value={row.creditUsage}
                          onChange={(e) =>
                            updatePageRangeEntry(
                              rowIndex,
                              "creditUsage",
                              e.target.value,
                              false,
                            )
                          }
                          min="0"
                          step="1"
                          className="modal-table-input"
                        />
                      );

                    case "actions":
                      return (
                        <button
                          type="button"
                          onClick={() => removePageRangeEntry(rowIndex, false)}
                          disabled={rowIndex === 0}
                          className="modal-table-remove-btn"
                          title={
                            rowIndex === 0
                              ? "Cannot remove first entry"
                              : "Remove this entry"
                          }
                        >
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      );

                    default:
                      return null;
                  }
                }}
              />
            </div>
          )}
          <div className="form-group">
            <label htmlFor="feedbackMode">Feedback Mode</label>
            <Select
              name="feedbackMode"
              options={feedbackModeOptions}
              value={feedbackModeOptions.find(
                (option) => option.value === formData.feedbackMode,
              )}
              onChange={handleSelectChange}
              placeholder="Select feedback mode..."
              isClearable
            />
          </div>
          <div className="form-group">
            <label>Allow Homework Grading</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="allowHomeworkGrading"
                checked={Boolean(formData.allowHomeworkGrading)}
                onChange={handleInputChange}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {formData.allowHomeworkGrading ? "Yes" : "No"}
              </span>
            </label>
          </div>
          <div className="form-group">
            <label htmlFor="slaMaxProcessingHours">SLA Max Processing Hours</label>
            <input
              type="number"
              id="slaMaxProcessingHours"
              name="slaMaxProcessingHours"
              value={formData.slaMaxProcessingHours}
              onChange={handleInputChange}
              placeholder="e.g. 2, 12, or 24"
              step="0.25"
              min="0"
            />
          </div>
          <div className="form-group">
            <label>Annotation Printing Offset Configurations</label>
            <ModalTableInput
              headers={[
                {
                  key: "name",
                  label: "Config Name",
                  width: "1.5fr",
                  required: true,
                },
                {
                  key: "topOffset",
                  label: "Top Offset",
                  width: "1fr",
                  required: true,
                },
                {
                  key: "rightOffset",
                  label: "Right Offset",
                  width: "1fr",
                  required: true,
                },
                {
                  key: "actions",
                  label: "Actions",
                  width: "0.6fr",
                },
              ]}
              rows={formData.annotationPrintingOffsetConfig || []}
              onRowChange={(index, field, value) =>
                updateOffsetConfigEntry(index, field, value, false)
              }
              onRowRemove={(index) => removeOffsetConfigEntry(index, false)}
              onRowAdd={() => addOffsetConfigEntry(false)}
              addButtonText="Add Offset Config"
              renderCell={(header, row, rowIndex) => {
                switch (header.key) {
                  case "name":
                    return (
                      <input
                        type="text"
                        value={row.name || ""}
                        onChange={(e) =>
                          updateOffsetConfigEntry(
                            rowIndex,
                            "name",
                            e.target.value,
                            false,
                          )
                        }
                        placeholder="e.g. Unit Test"
                        className="modal-table-input"
                      />
                    );
                  case "topOffset":
                    return (
                      <input
                        type="number"
                        value={row.topOffset ?? ""}
                        onChange={(e) =>
                          updateOffsetConfigEntry(
                            rowIndex,
                            "topOffset",
                            e.target.value,
                            false,
                          )
                        }
                        placeholder="e.g. 8.2"
                        step="any"
                        className="modal-table-input"
                      />
                    );
                  case "rightOffset":
                    return (
                      <input
                        type="number"
                        value={row.rightOffset ?? ""}
                        onChange={(e) =>
                          updateOffsetConfigEntry(
                            rowIndex,
                            "rightOffset",
                            e.target.value,
                            false,
                          )
                        }
                        placeholder="e.g. 0.2 cm"
                        step="any"

                        className="modal-table-input"
                      />
                    );
                  case "actions":
                    return (
                      <button
                        type="button"
                        onClick={() => removeOffsetConfigEntry(rowIndex, false)}
                        className="modal-table-remove-btn"
                        title="Remove this configuration"
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    );
                  default:
                    return null;
                }
              }}
            />
          </div>
          <div className="form-group">
            <label>AI Provider Configurations</label>
            <ModalTableInput
              headers={[
                {
                  key: "feature",
                  label: "Feature",
                  width: "1.5fr",
                  required: true,
                },
                {
                  key: "aiModelConfigId",
                  label: "AI Model Config",
                  width: "2.5fr",
                  required: true,
                },
                {
                  key: "actions",
                  label: "Actions",
                  width: "0.5fr",
                },
              ]}
              rows={formData.aiProviderConfig || []}
              onRowChange={(index, field, value) =>
                updateAIProviderConfigEntry(index, field, value, false)
              }
              onRowRemove={(index) => removeAIProviderConfigEntry(index, false)}
              onRowAdd={() => addAIProviderConfigEntry(false)}
              addButtonText="Add AI Provider Config"
              renderCell={(header, row, rowIndex) => {
                const matchingConfigs = (aiFeatureOptions.configs || []).filter(
                  (c) => c.type === row.feature || (['HomeWorkGrader', 'UPSCGrader', 'LanguageGrader'].includes(row.feature) && c.type === 'Grader')
                );

                switch (header.key) {
                  case "feature":
                    return (
                      <select
                        value={row.feature || ""}
                        onChange={(e) =>
                          updateAIProviderConfigEntry(
                            rowIndex,
                            "feature",
                            e.target.value,
                            false,
                          )
                        }
                        className="modal-table-input"
                      >
                        <option value="">Select Feature</option>
                        {aiFeatureOptions.features.map((feat) => (
                          <option key={feat} value={feat}>
                            {feat}
                          </option>
                        ))}
                      </select>
                    );
                  case "aiModelConfigId": {
                    const { gemini, openai, other } = groupConfigsByProvider(matchingConfigs);
                    return (
                      <select
                        value={row.aiModelConfigId || ""}
                        onChange={(e) =>
                          updateAIProviderConfigEntry(
                            rowIndex,
                            "aiModelConfigId",
                            e.target.value,
                            false,
                          )
                        }
                        className="modal-table-input"
                        disabled={!row.feature}
                      >
                        <option value="">Select AI Model Config</option>
                        {gemini.length > 0 && (
                          <optgroup label="Gemini">
                            {gemini.map((c) => (
                              <option key={c._id} value={c._id}>
                                {c.model || c.name}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {openai.length > 0 && (
                          <optgroup label="OpenAI">
                            {openai.map((c) => (
                              <option key={c._id} value={c._id}>
                                {c.model || c.name}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {other.length > 0 && (
                          <optgroup label="Other">
                            {other.map((c) => (
                              <option key={c._id} value={c._id}>
                                {c.model || c.name}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    );
                  }
                  case "actions":
                    return (
                      <button
                        type="button"
                        onClick={() => removeAIProviderConfigEntry(rowIndex, false)}
                        className="modal-table-remove-btn"
                        title="Remove this configuration"
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    );
                  default:
                    return null;
                }
              }}
            />
          </div>

          {/* isExternalBatchProcessing Enabled Toggle Switch Create Institute */}
          <div className="form-group">
            <label>External Batch Processing Enabled</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="isExternalBatchProcessing"
                checked={formData.isExternalBatchProcessing}
                onChange={handleInputChange}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {formData.isExternalBatchProcessing ? "Yes" : "No"}
              </span>
            </label>
          </div>

          <div className="form-group">
            <label htmlFor="curriculum">Curriculum</label>
            <Select
              name="curriculum"
              options={curriculumOptions}
              value={curriculumOptions.find(
                (option) => option.value === formData.curriculum,
              )}
              onChange={handleSelectChange}
              placeholder="Select curriculum..."
              isClearable
            />
          </div>
          <div className="form-group">
            <label htmlFor="domain">Domain</label>
            <input
              type="text"
              id="domain"
              name="domain"
              value={formData.domain}
              onChange={handleInputChange}
              placeholder="Enter domain"
            />
          </div>
          <div className="form-group">
            <label htmlFor="phoneNumber">Phone Number</label>
            <PhoneInput
              placeholder="Phone Number"
              country={userCountry}
              value={formData.phoneNumber}
              onChange={(value, data, event, formattedValue) => {
                setFormData((prev) => ({
                  ...prev,
                  phoneNumber: value,
                  formattedPhoneNumber: formattedValue,
                  country: data.name,
                }));
              }}
              inputStyle={{
                height: "40px",
                fontSize: "14px",
                width: "100%",
                paddingLeft: "48px",
              }}
              buttonStyle={{
                height: "40px",
              }}
              containerStyle={{
                width: "100%",
              }}
              enableSearch={true}
              disableSearchIcon={true}
              inputProps={{
                name: "phoneNumber",
              }}
              dropdownClass="phone-input-dropdown"
              dropdownStyle={{
                maxHeight: "200px",
                overflowX: "hidden",
              }}
            />
          </div>
          <div className="form-group">
            <label htmlFor="websiteURL">Website URL</label>
            <input
              type="url"
              id="websiteURL"
              name="websiteURL"
              value={formData.websiteURL}
              onChange={handleInputChange}
              placeholder="Enter website URL"
            />
          </div>
          <div className="form-group">
            <label htmlFor="whatsAppPhoneNumberId">
              WhatsApp Phone Number ID
            </label>
            <input
              type="text"
              id="whatsAppPhoneNumberId"
              name="whatsAppPhoneNumberId"
              value={formData.whatsAppPhoneNumberId}
              onChange={handleInputChange}
              placeholder="e.g. 794949640359788"
            />
          </div>
          <div className="form-group">
            <label htmlFor="whatsAppAccessToken">WhatsApp Access Token</label>
            <input
              id="whatsAppAccessToken"
              name="whatsAppAccessToken"
              value={formData.whatsAppAccessToken}
              onChange={handleInputChange}
              placeholder="Paste WhatsApp access token"
              rows={3}
            />
          </div>
          <div className="form-group">
            <label htmlFor="whatsAppNotificationLimit">WhatsApp Notification Limit</label>
            <input
              type="number"
              id="whatsAppNotificationLimit"
              name="whatsAppNotificationLimit"
              value={formData.whatsAppNotificationLimit}
              onChange={handleInputChange}
              placeholder="e.g. 100"
              min="0"
            />
          </div>
          <div className="form-group">
            <label htmlFor="logo">Institute Logo (jpg/png, max 1MB)</label>
            <input
              type="file"
              id="logo"
              name="logo"
              accept=".jpg,.jpeg,.png"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file && file.size > 1024 * 1024) {
                  setFormError("Logo file size must be less than 1MB.");
                  e.target.value = ""; // Clear the file input
                  setFormData((prev) => ({
                    ...prev,
                    logo: null,
                  }));
                  return;
                }
                setFormError("");
                setFormData((prev) => ({
                  ...prev,
                  logo: file,
                }));
              }}
            />
          </div>

          {/* isImageCreationAllowed Enabled Toggle Switch */}
          <div className="form-group">
            <label>Allow Image Creation</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="isImageCreationAllowed"
                checked={formData.isImageCreationAllowed}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    isImageCreationAllowed: e.target.checked,
                  }))
                }
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {formData.isImageCreationAllowed ? "Yes" : "No"}
              </span>
            </label>
          </div>
          {/* close */}

          {/* isManualGradingAllowed Enabled Toggle Switch Create Institute */}
          <div className="form-group">
            <label>Allow Manual Grading</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="isManualGradingAllowed"
                checked={formData.isManualGradingAllowed}
                onChange={handleInputChange}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {formData.isManualGradingAllowed ? "Yes" : "No"}
              </span>
            </label>
          </div>

          {/* isOnlineExamAllowed Enabled Toggle Switch Create Institute */}
          <div className="form-group">
            <label>Allow MCQ Exam</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="isOnlineExamAllowed"
                checked={formData.isOnlineExamAllowed}
                onChange={handleInputChange}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {formData.isOnlineExamAllowed ? "Yes" : "No"}
              </span>
            </label>
          </div>

          {/* isAdvancedOnlineExamAllowed Enabled Toggle Switch Create Institute */}
          <div className="form-group">
            <label>Allow Advanced Settings in Online Exam</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="isAdvancedOnlineExamAllowed"
                checked={formData.isAdvancedOnlineExamAllowed}
                onChange={handleInputChange}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {formData.isAdvancedOnlineExamAllowed ? "Yes" : "No"}
              </span>
            </label>
          </div>

          <div className="form-group">
            <label>Allow Report Card Generation</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="isReportCardEnabled"
                checked={formData.isReportCardEnabled}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    isReportCardEnabled: e.target.checked,
                  }))
                }
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {formData.isReportCardEnabled ? "Enabled" : "Disabled"}
              </span>
            </label>
          </div>

          <div className="form-group">
            <label htmlFor="reportCardTemplate">Report Card Template</label>
            <Select
              id="reportCardTemplate"
              name="reportCardTemplate"
              options={reportCardTemplateOptions}
              value={
                reportCardTemplateOptions.find(
                  (option) => option.value === formData.reportCardTemplate,
                ) || null
              }
              onChange={handleSelectChange}
              placeholder="Select report card template..."
              isClearable
            />
          </div>

          <div className="form-group">
            <label>Enable QR Page Count Detection</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="isQRPageCountDetectionEnabled"
                checked={formData.isQRPageCountDetectionEnabled}
                onChange={handleInputChange}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {formData.isQRPageCountDetectionEnabled ? "Enabled" : "Disabled"}
              </span>
            </label>
          </div>

          <div className="form-group">
            <label>Enable CO/PO Configuration</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="isCOPOEnabled"
                checked={formData.isCOPOEnabled}
                onChange={handleInputChange}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {formData.isCOPOEnabled ? "Enabled" : "Disabled"}
              </span>
            </label>
          </div>

          {formData.isCOPOEnabled && (
            <div
              className="copo-config-section"
              style={{
                marginTop: "20px",
                borderTop: "1px solid #eee",
                paddingTop: "10px",
              }}
            >
              <h3>CO/PO Configuration</h3>

              <div className="form-group">
                <label htmlFor="coAttainmentMethod">CO Attainment Method</label>
                <Select
                  name="coAttainmentMethod"
                  options={coAttainmentMethodOptions}
                  value={coAttainmentMethodOptions.find(
                    (option) => option.value === formData.coAttainmentMethod,
                  )}
                  onChange={handleSelectChange}
                  placeholder="Select attainment method..."
                  required={formData.isCOPOEnabled}
                />
              </div>

              {formData.coAttainmentMethod === "THRESHOLD_BASED_ATTAINMENT" && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "10px",
                    backgroundColor: "#f9f9f9",
                    padding: "10px",
                    borderRadius: "5px",
                    marginBottom: "10px",
                  }}
                >
                  <div className="form-group">
                    <label htmlFor="minScorePercentage">Min Score %</label>
                    <input
                      type="number"
                      id="minScorePercentage"
                      name="minScorePercentage"
                      value={formData.minScorePercentage}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="level1MinStudentPercentage">
                      Level 1 Min Student %
                    </label>
                    <input
                      type="number"
                      id="level1MinStudentPercentage"
                      name="level1MinStudentPercentage"
                      value={formData.level1MinStudentPercentage}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="level2MinStudentPercentage">
                      Level 2 Min Student %
                    </label>
                    <input
                      type="number"
                      id="level2MinStudentPercentage"
                      name="level2MinStudentPercentage"
                      value={formData.level2MinStudentPercentage}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="level3MinStudentPercentage">
                      Level 3 Min Student %
                    </label>
                    <input
                      type="number"
                      id="level3MinStudentPercentage"
                      name="level3MinStudentPercentage"
                      value={formData.level3MinStudentPercentage}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                    />
                  </div>
                </div>
              )}

              {formData.coAttainmentMethod === "SCORE_BASED_ATTAINMENT" && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "10px",
                    backgroundColor: "#f9f9f9",
                    padding: "10px",
                    borderRadius: "5px",
                    marginBottom: "10px",
                  }}
                >
                  <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                    <label htmlFor="scoreAveragingBasis">
                      Score Averaging Basis
                    </label>
                    <Select
                      name="scoreAveragingBasis"
                      options={scoreAveragingBasisOptions}
                      value={scoreAveragingBasisOptions.find(
                        (option) =>
                          option.value === formData.scoreAveragingBasis,
                      )}
                      onChange={handleSelectChange}
                      placeholder="Select averaging basis..."
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="level1MinScorePercentage">
                      Level 1 Min Score %
                    </label>
                    <input
                      type="number"
                      id="level1MinScorePercentage"
                      name="level1MinScorePercentage"
                      value={formData.level1MinScorePercentage}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="level2MinScorePercentage">
                      Level 2 Min Score %
                    </label>
                    <input
                      type="number"
                      id="level2MinScorePercentage"
                      name="level2MinScorePercentage"
                      value={formData.level2MinScorePercentage}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="level3MinScorePercentage">
                      Level 3 Min Score %
                    </label>
                    <input
                      type="number"
                      id="level3MinScorePercentage"
                      name="level3MinScorePercentage"
                      value={formData.level3MinScorePercentage}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                    />
                  </div>
                </div>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px",
                }}
              >
                <div className="form-group">
                  <label htmlFor="internalCOWeightage">
                    Internal CO Weightage
                  </label>
                  <input
                    type="number"
                    id="internalCOWeightage"
                    name="internalCOWeightage"
                    value={formData.internalCOWeightage}
                    onChange={handleInputChange}
                    min="0"
                    max="1"
                    step="0.25"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="externalCOWeightage">
                    External CO Weightage
                  </label>
                  <input
                    type="number"
                    id="externalCOWeightage"
                    name="externalCOWeightage"
                    value={formData.externalCOWeightage}
                    onChange={handleInputChange}
                    min="0"
                    max="1"
                    step="0.25"
                  />
                </div>
              </div>
              {!validateCOWeightage(
                formData.internalCOWeightage,
                formData.externalCOWeightage,
              ).isValid && (
                <p
                  style={{
                    color: "#dc3545",
                    fontSize: "0.85rem",
                    margin: "4px 0 10px 0",
                    fontWeight: "500",
                  }}
                >
                  {
                    validateCOWeightage(
                      formData.internalCOWeightage,
                      formData.externalCOWeightage,
                    ).message
                  }
                </p>
              )}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px",
                }}
              >
                <div className="form-group">
                  <label htmlFor="directCOWeightage">Direct CO Weightage</label>
                  <input
                    type="number"
                    id="directCOWeightage"
                    name="directCOWeightage"
                    value={formData.directCOWeightage}
                    onChange={handleInputChange}
                    min="0"
                    max="1"
                    step="0.25"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="indirectCOWeightage">
                    Indirect CO Weightage
                  </label>
                  <input
                    type="number"
                    id="indirectCOWeightage"
                    name="indirectCOWeightage"
                    value={formData.indirectCOWeightage}
                    onChange={handleInputChange}
                    min="0"
                    max="1"
                    step="0.25"
                  />
                </div>
              </div>
              {!validateDirectIndirectCOWeightage(
                formData.directCOWeightage,
                formData.indirectCOWeightage,
              ).isValid && (
                <p
                  style={{
                    color: "#dc3545",
                    fontSize: "0.85rem",
                    margin: "4px 0 10px 0",
                    fontWeight: "500",
                  }}
                >
                  {
                    validateDirectIndirectCOWeightage(
                      formData.directCOWeightage,
                      formData.indirectCOWeightage,
                    ).message
                  }
                </p>
              )}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px",
                }}
              >
                <div className="form-group">
                  <label htmlFor="directPOWeightage">Direct PO Weightage</label>
                  <input
                    type="number"
                    id="directPOWeightage"
                    name="directPOWeightage"
                    value={formData.directPOWeightage}
                    onChange={handleInputChange}
                    min="0"
                    max="1"
                    step="0.25"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="indirectPOWeightage">
                    Indirect PO Weightage
                  </label>
                  <input
                    type="number"
                    id="indirectPOWeightage"
                    name="indirectPOWeightage"
                    value={formData.indirectPOWeightage}
                    onChange={handleInputChange}
                    min="0"
                    max="1"
                    step="0.25"
                  />
                </div>
              </div>
              {!validateDirectIndirectPOWeightage(
                formData.directPOWeightage,
                formData.indirectPOWeightage,
              ).isValid && (
                <p
                  style={{
                    color: "#dc3545",
                    fontSize: "0.85rem",
                    margin: "4px 0 10px 0",
                    fontWeight: "500",
                  }}
                >
                  {
                    validateDirectIndirectPOWeightage(
                      formData.directPOWeightage,
                      formData.indirectPOWeightage,
                    ).message
                  }
                </p>
              )}
            </div>
          )}

          {/* hasSubscribed Enabled Toggle Switch */}
          <div className="form-group">
            <label>Has Subscribed </label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="hasSubscribed"
                checked={formData.hasSubscribed}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    hasSubscribed: e.target.checked,
                  }))
                }
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {formData.hasSubscribed ? "Yes" : "No"}
              </span>
            </label>
          </div>
          {/* close */}

          {/* isCreditReportEnabled Toggle Switch */}
          <div className="form-group">
            <label>Enable Credit Report</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="isCreditReportEnabled"
                checked={formData.isCreditReportEnabled}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    isCreditReportEnabled: e.target.checked,
                  }))
                }
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {formData.isCreditReportEnabled ? "Yes" : "No"}
              </span>
            </label>
          </div>

          {/* Allow Student Class Join Toggle Switch */}
          <div className="form-group">
            <label>Allow Students to Join Class</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="allowStudentClassJoin"
                checked={formData.allowStudentClassJoin}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    allowStudentClassJoin: e.target.checked,
                  }))
                }
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {formData.allowStudentClassJoin ? "Yes" : "No"}
              </span>
            </label>
          </div>

          {/* Auto Generate Student Password Toggle Switch */}
          <div className="form-group">
            <label>Auto Generate Student Password</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="autoGenerateStudentPassword"
                checked={formData.autoGenerateStudentPassword}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    autoGenerateStudentPassword: e.target.checked,
                  }))
                }
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {formData.autoGenerateStudentPassword ? "Yes" : "No"}
              </span>
            </label>
          </div>

          {/* Webhook Enabled Toggle Switch */}
          <div className="form-group">
            <label>Webhook Enabled</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                checked={formData.webhookEnabled}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    webhookEnabled: e.target.checked,
                  }))
                }
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {formData.webhookEnabled ? "Yes" : "No"}
              </span>
            </label>
          </div>

          {/* close */}
          {formData.webhookEnabled && (
            <div className="form-group">
              <label htmlFor="webhookURL">Webhook URL</label>
              <input
                type="url"
                id="webhookURL"
                name="webhookURL"
                value={formData.webhookURL}
                onChange={handleInputChange}
                placeholder="Enter webhook URL"
              />
            </div>
          )}
          <div className="form-group">
            <label htmlFor="websiteURL">Data Region</label>
            <Select
              name="dataRegion"
              options={dataRegionOptions}
              value={dataRegionOptions.find(
                (option) => option.value === formData.dataRegion,
              )}
              onChange={handleSelectChange}
              placeholder="Select region..."
              isClearable
            />
          </div>

          {formError && <div className="error-message">{formError}</div>}
          <button type="submit" className="btn-submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <Loader
                style={{ fontSize: "1em", color: "white", top: "0", left: "0" }}
              />
            ) : (
              <>
                <i className="fa-solid fa-plus"></i> Create Institute
              </>
            )}
          </button>
        </form>
      </Modal>

      {/* Edit Institute Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          if (!isSubmitting) {
            setIsEditModalOpen(false);
            setInitialEditFormData(null);
          }
        }}
        maxWidth="1000px"
      >
        <h2>Edit Institute</h2>
        <form onSubmit={handleEditInstitute} encType="multipart/form-data">
          <div className="form-group">
            <label htmlFor="edit-name">
              <i className="fa-solid fa-asterisk requiredIcon"></i> Institute
              Name
            </label>
            <input
              type="text"
              id="edit-name"
              name="name"
              value={editFormData.name}
              onChange={handleEditInputChange}
              required
              placeholder="Edit institute name"
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit-tagline">Tagline</label>
            <input
              type="text"
              id="edit-tagline"
              name="tagline"
              value={editFormData.tagline}
              onChange={handleEditInputChange}
              placeholder="Edit institute tagline"
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit-address">Address</label>
            <input
              type="text"
              id="edit-address"
              name="address"
              value={editFormData.address}
              onChange={handleEditInputChange}
              placeholder="Edit institute address"
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit-code">
              <i className="fa-solid fa-asterisk requiredIcon"></i> Institute
              Code
            </label>
            <input
              type="text"
              id="edit-code"
              name="code"
              value={editFormData.code}
              onChange={handleEditInputChange}
              required
              placeholder="Edit institute code"
            />
          </div>

          <div className="form-group">
            <label htmlFor="category"><i className="fa-solid fa-asterisk requiredIcon"></i> Category</label>
            <Select
              id="edit-category"
              name="category"
              options={categoryOptions}
              value={categoryOptions.find(
                (option) => option.value === editFormData.category,
              )}
              onChange={handleEditSelectChange}
              placeholder="Select category..."
              required
              isClearable
            />
          </div>

          <div className="form-group">
            <label>Standardized Test Templates</label>
            <Select
              isMulti
              name="standardizedTestTemplateIds"
              options={standardizedTemplatesOptions}
              value={editFormData.standardizedTestTemplateIds
                ?.map((id) =>
                  standardizedTemplatesOptions.find(
                    (option) => option.value === id,
                  ),
                )
                .filter(Boolean)}
              onChange={(selectedOptions) => {
                setEditFormData((prev) => ({
                  ...prev,
                  standardizedTestTemplateIds: selectedOptions
                    ? selectedOptions.map((o) => o.value)
                    : [],
                }));
              }}
              placeholder="Select templates..."
              isClearable
            />
          </div>

          {/* Page Range Credit Usage Rules Enabled Toggle Switch */}
          <div className="form-group">
            <label>Page Range Credit Usage Rules Enabled</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="isPageRangeRulesEnabled"
                checked={editFormData.isPageRangeRulesEnabled}
                onChange={handleEditInputChange}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {editFormData.isPageRangeRulesEnabled ? "Yes" : "No"}
              </span>
            </label>
          </div>

          {/* Page Range Credit Usage Rules */}
          {editFormData.isPageRangeRulesEnabled && (
            <div className="form-group">
              <label>Page Range Credit Usage Rules</label>
              <ModalTableInput
                headers={[
                  {
                    key: "minPageCount",
                    label: "Min Page Count",
                    width: "1fr",
                    required: true,
                  },
                  {
                    key: "maxPageCount",
                    label: "Max Page Count",
                    width: "1fr",
                    required: true,
                  },
                  {
                    key: "creditUsage",
                    label: "Credit Usage",
                    width: "1fr",
                    required: true,
                  },
                  {
                    key: "actions",
                    label: "Actions",
                    width: "0.8fr",
                  },
                ]}
                rows={editFormData.pageRangeCreditUsageRules}
                onRowChange={(index, field, value) =>
                  updatePageRangeEntry(index, field, value, true)
                }
                onRowRemove={(index) => removePageRangeEntry(index, true)}
                onRowAdd={() => addPageRangeEntry(true)}
                canRemove={true}
                addButtonText="Add Entry"
                renderCell={(header, row, rowIndex) => {
                  switch (header.key) {
                    case "minPageCount":
                      return (
                        <input
                          type="number"
                          value={row.minPageCount}
                          disabled={rowIndex === 0}
                          onChange={(e) =>
                            updatePageRangeEntry(
                              rowIndex,
                              "minPageCount",
                              e.target.value,
                              true,
                            )
                          }
                          min="1"
                          step="1"
                          className="modal-table-input"
                          style={{
                            opacity: rowIndex === 0 ? 0.6 : 1,
                            cursor: rowIndex === 0 ? "not-allowed" : "text",
                          }}
                        />
                      );

                    case "maxPageCount":
                      return (
                        <input
                          type="number"
                          value={row.maxPageCount}
                          onChange={(e) =>
                            updatePageRangeEntry(
                              rowIndex,
                              "maxPageCount",
                              e.target.value,
                              true,
                            )
                          }
                          min={row.minPageCount}
                          step="1"
                          className="modal-table-input"
                        />
                      );

                    case "creditUsage":
                      return (
                        <input
                          type="number"
                          value={row.creditUsage}
                          onChange={(e) =>
                            updatePageRangeEntry(
                              rowIndex,
                              "creditUsage",
                              e.target.value,
                              true,
                            )
                          }
                          min="0"
                          step="1"
                          className="modal-table-input"
                        />
                      );

                    case "actions":
                      return (
                        <button
                          type="button"
                          onClick={() => removePageRangeEntry(rowIndex, true)}
                          disabled={rowIndex === 0}
                          className="modal-table-remove-btn"
                          title={
                            rowIndex === 0
                              ? "Cannot remove first entry"
                              : "Remove this entry"
                          }
                        >
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      );

                    default:
                      return null;
                  }
                }}
              />
            </div>
          )}
          <div className="form-group">
            <label htmlFor="edit-feedbackMode">Feedback Mode</label>
            <Select
              id="edit-feedbackMode"
              name="feedbackMode"
              options={feedbackModeOptions}
              value={feedbackModeOptions.find(
                (option) => option.value === editFormData.feedbackMode,
              )}
              onChange={handleEditSelectChange}
              placeholder="Select feedback mode..."
              isClearable
            />
          </div>
          <div className="form-group">
            <label>Allow Homework Grading</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="allowHomeworkGrading"
                checked={Boolean(editFormData.allowHomeworkGrading)}
                onChange={handleEditInputChange}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {editFormData.allowHomeworkGrading ? "Yes" : "No"}
              </span>
            </label>
          </div>
          <div className="form-group">
            <label htmlFor="edit-slaMaxProcessingHours">SLA Max Processing Hours</label>
            <input
              type="number"
              id="edit-slaMaxProcessingHours"
              name="slaMaxProcessingHours"
              value={editFormData.slaMaxProcessingHours}
              onChange={handleEditInputChange}
              placeholder="e.g. 2, 12, or 24"
              step="0.25"
              min="0"
            />
          </div>
          <div className="form-group">
            <label>Annotation Printing Offset Configurations</label>
            <ModalTableInput
              headers={[
                {
                  key: "name",
                  label: "Config Name",
                  width: "1.5fr",
                  required: true,
                },
                {
                  key: "topOffset",
                  label: "Top Offset",
                  width: "1fr",
                  required: true,
                },
                {
                  key: "rightOffset",
                  label: "Right Offset",
                  width: "1fr",
                  required: true,
                },
                {
                  key: "actions",
                  label: "Actions",
                  width: "0.6fr",
                },
              ]}
              rows={editFormData.annotationPrintingOffsetConfig || []}
              onRowChange={(index, field, value) =>
                updateOffsetConfigEntry(index, field, value, true)
              }
              onRowRemove={(index) => removeOffsetConfigEntry(index, true)}
              onRowAdd={() => addOffsetConfigEntry(true)}
              addButtonText="Add Offset Config"
              renderCell={(header, row, rowIndex) => {
                switch (header.key) {
                  case "name":
                    return (
                      <input
                        type="text"
                        value={row.name || ""}
                        onChange={(e) =>
                          updateOffsetConfigEntry(
                            rowIndex,
                            "name",
                            e.target.value,
                            true,
                          )
                        }
                        placeholder="e.g. Unit Test"
                        className="modal-table-input"
                      />
                    );
                  case "topOffset":
                    return (
                      <input
                        type="number"
                        value={row.topOffset ?? ""}
                        onChange={(e) =>
                          updateOffsetConfigEntry(
                            rowIndex,
                            "topOffset",
                            e.target.value,
                            true,
                          )
                        }
                        placeholder="e.g. 0.4 in cm"
                        step="any"
                        className="modal-table-input"
                      />
                    );
                  case "rightOffset":
                    return (
                      <input
                        type="number"
                        value={row.rightOffset ?? ""}
                        onChange={(e) =>
                          updateOffsetConfigEntry(
                            rowIndex,
                            "rightOffset",
                            e.target.value,
                            true,
                          )
                        }
                        placeholder="e.g. 0.2 in cm"
                        step="any"
                        className="modal-table-input"
                      />
                    );
                  case "actions":
                    return (
                      <button
                        type="button"
                        onClick={() => removeOffsetConfigEntry(rowIndex, true)}
                        className="modal-table-remove-btn"
                        title="Remove this configuration"
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    );
                  default:
                    return null;
                }
              }}
            />
          </div>
          
          <div className="form-group">
            <label>AI Provider Configurations</label>
            <ModalTableInput
              headers={[
                {
                  key: "feature",
                  label: "Feature",
                  width: "1.5fr",
                  required: true,
                },
                {
                  key: "aiModelConfigId",
                  label: "AI Model Config",
                  width: "2.5fr",
                  required: true,
                },
                {
                  key: "actions",
                  label: "Actions",
                  width: "0.5fr",
                },
              ]}
              rows={editFormData.aiProviderConfig || []}
              onRowChange={(index, field, value) =>
                updateAIProviderConfigEntry(index, field, value, true)
              }
              onRowRemove={(index) => removeAIProviderConfigEntry(index, true)}
              onRowAdd={() => addAIProviderConfigEntry(true)}
              addButtonText="Add AI Provider Config"
              renderCell={(header, row, rowIndex) => {
                const matchingConfigs = (aiFeatureOptions.configs || []).filter(
                  (c) => c.type === row.feature || (['HomeWorkGrader', 'UPSCGrader', 'LanguageGrader'].includes(row.feature) && c.type === 'Grader')
                );

                switch (header.key) {
                  case "feature":
                    return (
                      <select
                        value={row.feature || ""}
                        onChange={(e) =>
                          updateAIProviderConfigEntry(
                            rowIndex,
                            "feature",
                            e.target.value,
                            true,
                          )
                        }
                        className="modal-table-input"
                      >
                        <option value="">Select Feature</option>
                        {aiFeatureOptions.features.map((feat) => (
                          <option key={feat} value={feat}>
                            {feat}
                          </option>
                        ))}
                      </select>
                    );
                  case "aiModelConfigId": {
                    const { gemini, openai, other } = groupConfigsByProvider(matchingConfigs);
                    return (
                      <select
                        value={row.aiModelConfigId || ""}
                        onChange={(e) =>
                          updateAIProviderConfigEntry(
                            rowIndex,
                            "aiModelConfigId",
                            e.target.value,
                            true,
                          )
                        }
                        className="modal-table-input"
                        disabled={!row.feature}
                      >
                        <option value="">Select AI Model Config</option>
                        {gemini.length > 0 && (
                          <optgroup label="Gemini">
                            {gemini.map((c) => (
                              <option key={c._id} value={c._id}>
                                {c.model || c.name}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {openai.length > 0 && (
                          <optgroup label="OpenAI">
                            {openai.map((c) => (
                              <option key={c._id} value={c._id}>
                                {c.model || c.name}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {other.length > 0 && (
                          <optgroup label="Other">
                            {other.map((c) => (
                              <option key={c._id} value={c._id}>
                                {c.model || c.name}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    );
                  }
                  case "actions":
                    return (
                      <button
                        type="button"
                        onClick={() => removeAIProviderConfigEntry(rowIndex, true)}
                        className="modal-table-remove-btn"
                        title="Remove this configuration"
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    );
                  default:
                    return null;
                }
              }}
            />
          </div>

          {/* isExternalBatchProcessing Enabled Toggle Switch Edit Institute */}
          <div className="form-group">
            <label>External Batch Processing Enabled</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="isExternalBatchProcessing"
                checked={editFormData.isExternalBatchProcessing}
                onChange={handleEditInputChange}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {editFormData.isExternalBatchProcessing ? "Yes" : "No"}
              </span>
            </label>
          </div>
          
          <div className="form-group">
            <label htmlFor="edit-curriculum">Curriculum</label>
            <Select
              id="edit-curriculum"
              name="curriculum"
              options={curriculumOptions}
              value={curriculumOptions.find(
                (option) => option.value === editFormData.curriculum,
              )}
              onChange={handleEditSelectChange}
              placeholder="Select curriculum..."
              isClearable
            />
          </div>

          <div className="form-group">
            <label htmlFor="edit-domain">Domain</label>
            <input
              type="text"
              id="edit-domain"
              name="domain"
              value={editFormData.domain}
              onChange={handleEditInputChange}
              placeholder="Edit domain"
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit-phoneNumber">Phone Number</label>
            <PhoneInput
              placeholder="Phone Number"
              country={userCountry}
              value={editFormData.phoneNumber}
              onChange={(value, data, event, formattedValue) => {
                setEditFormData((prev) => ({
                  ...prev,
                  phoneNumber: value,
                  formattedPhoneNumber: formattedValue,
                  country: data?.name || "",
                }));
              }}
              inputStyle={{
                height: "40px",
                fontSize: "14px",
                width: "100%",
                paddingLeft: "48px",
              }}
              buttonStyle={{
                height: "40px",
              }}
              containerStyle={{
                width: "100%",
              }}
              enableSearch={true}
              disableSearchIcon={true}
              inputProps={{
                name: "phoneNumber",
              }}
              dropdownClass="phone-input-dropdown"
              dropdownStyle={{
                maxHeight: "200px",
                overflowX: "hidden",
              }}
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit-websiteURL">Website URL</label>
            <input
              type="url"
              id="edit-websiteURL"
              name="websiteURL"
              value={editFormData.websiteURL}
              onChange={handleEditInputChange}
              placeholder="Edit website URL"
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit-whatsAppPhoneNumberId">
              WhatsApp Phone Number ID
            </label>
            <input
              type="text"
              id="edit-whatsAppPhoneNumberId"
              name="whatsAppPhoneNumberId"
              value={editFormData.whatsAppPhoneNumberId}
              onChange={handleEditInputChange}
              placeholder="e.g. 794949640359788"
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit-whatsAppAccessToken">
              WhatsApp Access Token
            </label>
            <input
              id="edit-whatsAppAccessToken"
              name="whatsAppAccessToken"
              value={editFormData.whatsAppAccessToken}
              onChange={handleEditInputChange}
              placeholder="Paste WhatsApp access token"
              rows={3}
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit-whatsAppNotificationLimit">WhatsApp Notification Limit</label>
            <input
              type="number"
              id="edit-whatsAppNotificationLimit"
              name="whatsAppNotificationLimit"
              value={editFormData.whatsAppNotificationLimit}
              onChange={handleEditInputChange}
              placeholder="e.g. 100"
              min="0"
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit-logo">Institute Logo (jpg/png, max 1MB)</label>
            <input
              type="file"
              id="edit-logo"
              name="logo"
              accept=".jpg,.jpeg,.png"
              onChange={handleEditInputChange}
            />
            {/* Show current logo filename */}
            {editFormData.logoURL && (
              <div
                style={{ marginTop: "8px", fontSize: "0.95em", color: "#555" }}
              >
                Current logo: <b>{editFormData.logoURL.split("/").pop()}</b>
              </div>
            )}
            {/* Show preview if logoURL exists */}
            {editFormData.logoURL && (
              <div style={{ marginTop: "8px" }}>
                <img
                  src={editFormData.logoURL}
                  alt="Current Logo"
                  style={{ maxWidth: "100px", maxHeight: "100px" }}
                />
              </div>
            )}
            {/* Remove logo button */}
            {editFormData.logoURL && (
              <button
                type="button"
                className="remove-logo-btn"
                style={{
                  marginTop: "8px",
                  background: "#e74c3c",
                  color: "#fff",
                  border: "none",
                  padding: "6px 12px",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
                onClick={handleRemoveLogo}
                disabled={isSubmitting}
              >
                <i className="fa-solid fa-xmark"></i> Remove Logo
              </button>
            )}
          </div>

          {/* isImageCreation Enabled Toggle Switch  Edit Institute*/}
          <div className="form-group">
            <label>Allow Image Creation</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="isImageCreationAllowed"
                checked={editFormData.isImageCreationAllowed}
                onChange={handleEditInputChange}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {editFormData.isImageCreationAllowed ? "Yes" : "No"}
              </span>
            </label>
          </div>
          {/* isImageCreation is Allowed Closing  */}

          {/* isManualGradingAllowed Enabled Toggle Switch Edit Institute */}
          <div className="form-group">
            <label>Allow Manual Grading</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="isManualGradingAllowed"
                checked={editFormData.isManualGradingAllowed}
                onChange={handleEditInputChange}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {editFormData.isManualGradingAllowed ? "Yes" : "No"}
              </span>
            </label>
          </div>

          {/* isOnlineExamAllowed Enabled Toggle Switch Edit Institute */}
          <div className="form-group">
            <label>Allow MCQ Exam</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="isOnlineExamAllowed"
                checked={editFormData.isOnlineExamAllowed}
                onChange={handleEditInputChange}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {editFormData.isOnlineExamAllowed ? "Yes" : "No"}
              </span>
            </label>
          </div>

          {/* isAdvancedOnlineExamAllowed Enabled Toggle Switch Edit Institute */}
          <div className="form-group">
            <label>Allow Advanced Settings in Online Exam</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="isAdvancedOnlineExamAllowed"
                checked={editFormData.isAdvancedOnlineExamAllowed}
                onChange={handleEditInputChange}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {editFormData.isAdvancedOnlineExamAllowed ? "Yes" : "No"}
              </span>
            </label>
          </div>

          <div className="form-group">
            <label>Allow Report Card Generation</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="isReportCardEnabled"
                checked={editFormData.isReportCardEnabled}
                onChange={(e) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    isReportCardEnabled: e.target.checked,
                  }))
                }
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {editFormData.isReportCardEnabled ? "Enabled" : "Disabled"}
              </span>
            </label>
          </div>

          <div className="form-group">
            <label htmlFor="edit-reportCardTemplate">Report Card Template</label>
            <Select
              id="edit-reportCardTemplate"
              name="reportCardTemplate"
              options={reportCardTemplateOptions}
              value={
                reportCardTemplateOptions.find(
                  (option) => option.value === editFormData.reportCardTemplate,
                ) || null
              }
              onChange={handleEditSelectChange}
              placeholder="Select report card template..."
              isClearable
            />
          </div>

          <div className="form-group">
            <label>Enable QR Page Count Detection</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="isQRPageCountDetectionEnabled"
                checked={editFormData.isQRPageCountDetectionEnabled}
                onChange={handleEditInputChange}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {editFormData.isQRPageCountDetectionEnabled ? "Enabled" : "Disabled"}
              </span>
            </label>
          </div>

          <div className="form-group">
            <label>Enable CO/PO Configuration</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="isCOPOEnabled"
                checked={editFormData.isCOPOEnabled}
                onChange={handleEditInputChange}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {editFormData.isCOPOEnabled ? "Enabled" : "Disabled"}
              </span>
            </label>
          </div>

          {editFormData.isCOPOEnabled && (
            <div
              className="copo-config-section"
              style={{
                marginTop: "20px",
                borderTop: "1px solid #eee",
                paddingTop: "10px",
              }}
            >
              <h3>CO/PO Configuration</h3>

              <div className="form-group">
                <label htmlFor="edit-coAttainmentMethod">
                  CO Attainment Method
                </label>
                <Select
                  name="coAttainmentMethod"
                  options={coAttainmentMethodOptions}
                  value={coAttainmentMethodOptions.find(
                    (option) =>
                      option.value === editFormData.coAttainmentMethod,
                  )}
                  onChange={handleEditSelectChange}
                  placeholder="Select attainment method..."
                  required={editFormData.isCOPOEnabled}
                />
              </div>

              {editFormData.coAttainmentMethod ===
                "THRESHOLD_BASED_ATTAINMENT" && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "10px",
                    backgroundColor: "#f9f9f9",
                    padding: "10px",
                    borderRadius: "5px",
                    marginBottom: "10px",
                  }}
                >
                  <div className="form-group">
                    <label htmlFor="edit-minScorePercentage">Min Score %</label>
                    <input
                      type="number"
                      id="edit-minScorePercentage"
                      name="minScorePercentage"
                      value={editFormData.minScorePercentage}
                      onChange={handleEditInputChange}
                      min="0"
                      max="100"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-level1MinStudentPercentage">
                      Level 1 Min Student %
                    </label>
                    <input
                      type="number"
                      id="edit-level1MinStudentPercentage"
                      name="level1MinStudentPercentage"
                      value={editFormData.level1MinStudentPercentage}
                      onChange={handleEditInputChange}
                      min="0"
                      max="100"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-level2MinStudentPercentage">
                      Level 2 Min Student %
                    </label>
                    <input
                      type="number"
                      id="edit-level2MinStudentPercentage"
                      name="level2MinStudentPercentage"
                      value={editFormData.level2MinStudentPercentage}
                      onChange={handleEditInputChange}
                      min="0"
                      max="100"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-level3MinStudentPercentage">
                      Level 3 Min Student %
                    </label>
                    <input
                      type="number"
                      id="edit-level3MinStudentPercentage"
                      name="level3MinStudentPercentage"
                      value={editFormData.level3MinStudentPercentage}
                      onChange={handleEditInputChange}
                      min="0"
                      max="100"
                    />
                  </div>
                </div>
              )}

              {editFormData.coAttainmentMethod === "SCORE_BASED_ATTAINMENT" && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "10px",
                    backgroundColor: "#f9f9f9",
                    padding: "10px",
                    borderRadius: "5px",
                    marginBottom: "10px",
                  }}
                >
                  <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                    <label htmlFor="edit-scoreAveragingBasis">
                      Score Averaging Basis
                    </label>
                    <Select
                      name="scoreAveragingBasis"
                      options={scoreAveragingBasisOptions}
                      value={scoreAveragingBasisOptions.find(
                        (option) =>
                          option.value === editFormData.scoreAveragingBasis,
                      )}
                      onChange={handleEditSelectChange}
                      placeholder="Select averaging basis..."
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-level1MinScorePercentage">
                      Level 1 Min Score %
                    </label>
                    <input
                      type="number"
                      id="edit-level1MinScorePercentage"
                      name="level1MinScorePercentage"
                      value={editFormData.level1MinScorePercentage}
                      onChange={handleEditInputChange}
                      min="0"
                      max="100"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-level2MinScorePercentage">
                      Level 2 Min Score %
                    </label>
                    <input
                      type="number"
                      id="edit-level2MinScorePercentage"
                      name="level2MinScorePercentage"
                      value={editFormData.level2MinScorePercentage}
                      onChange={handleEditInputChange}
                      min="0"
                      max="100"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-level3MinScorePercentage">
                      Level 3 Min Score %
                    </label>
                    <input
                      type="number"
                      id="edit-level3MinScorePercentage"
                      name="level3MinScorePercentage"
                      value={editFormData.level3MinScorePercentage}
                      onChange={handleEditInputChange}
                      min="0"
                      max="100"
                    />
                  </div>
                </div>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px",
                }}
              >
                <div className="form-group">
                  <label htmlFor="edit-internalCOWeightage">
                    Internal CO Weightage
                  </label>
                  <input
                    type="number"
                    id="edit-internalCOWeightage"
                    name="internalCOWeightage"
                    value={editFormData.internalCOWeightage}
                    onChange={handleEditInputChange}
                    min="0"
                    max="1"
                    step="0.25"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="edit-externalCOWeightage">
                    External CO Weightage
                  </label>
                  <input
                    type="number"
                    id="edit-externalCOWeightage"
                    name="externalCOWeightage"
                    value={editFormData.externalCOWeightage}
                    onChange={handleEditInputChange}
                    min="0"
                    max="1"
                    step="0.25"
                  />
                </div>
              </div>
              {!validateCOWeightage(
                editFormData.internalCOWeightage,
                editFormData.externalCOWeightage,
              ).isValid && (
                <p
                  style={{
                    color: "#dc3545",
                    fontSize: "0.85rem",
                    margin: "4px 0 10px 0",
                    fontWeight: "500",
                  }}
                >
                  {
                    validateCOWeightage(
                      editFormData.internalCOWeightage,
                      editFormData.externalCOWeightage,
                    ).message
                  }
                </p>
              )}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px",
                }}
              >
                <div className="form-group">
                  <label htmlFor="edit-directCOWeightage">
                    Direct CO Weightage
                  </label>
                  <input
                    type="number"
                    id="edit-directCOWeightage"
                    name="directCOWeightage"
                    value={editFormData.directCOWeightage}
                    onChange={handleEditInputChange}
                    min="0"
                    max="1"
                    step="0.25"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="edit-indirectCOWeightage">
                    Indirect CO Weightage
                  </label>
                  <input
                    type="number"
                    id="edit-indirectCOWeightage"
                    name="indirectCOWeightage"
                    value={editFormData.indirectCOWeightage}
                    onChange={handleEditInputChange}
                    min="0"
                    max="1"
                    step="0.25"
                  />
                </div>
              </div>
              {!validateDirectIndirectCOWeightage(
                editFormData.directCOWeightage,
                editFormData.indirectCOWeightage,
              ).isValid && (
                <p
                  style={{
                    color: "#dc3545",
                    fontSize: "0.85rem",
                    margin: "4px 0 10px 0",
                    fontWeight: "500",
                  }}
                >
                  {
                    validateDirectIndirectCOWeightage(
                      editFormData.directCOWeightage,
                      editFormData.indirectCOWeightage,
                    ).message
                  }
                </p>
              )}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "10px",
                }}
              >
                <div className="form-group">
                  <label htmlFor="edit-directPOWeightage">
                    Direct PO Weightage
                  </label>
                  <input
                    type="number"
                    id="edit-directPOWeightage"
                    name="directPOWeightage"
                    value={editFormData.directPOWeightage}
                    onChange={handleEditInputChange}
                    min="0"
                    max="1"
                    step="0.25"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="edit-indirectPOWeightage">
                    Indirect PO Weightage
                  </label>
                  <input
                    type="number"
                    id="edit-indirectPOWeightage"
                    name="indirectPOWeightage"
                    value={editFormData.indirectPOWeightage}
                    onChange={handleEditInputChange}
                    min="0"
                    max="1"
                    step="0.25"
                  />
                </div>
              </div>
              {!validateDirectIndirectPOWeightage(
                editFormData.directPOWeightage,
                editFormData.indirectPOWeightage,
              ).isValid && (
                <p
                  style={{
                    color: "#dc3545",
                    fontSize: "0.85rem",
                    margin: "4px 0 10px 0",
                    fontWeight: "500",
                  }}
                >
                  {
                    validateDirectIndirectPOWeightage(
                      editFormData.directPOWeightage,
                      editFormData.indirectPOWeightage,
                    ).message
                  }
                </p>
              )}
            </div>
          )}

          {/* hasSubscribed Enabled Toggle Switch  Edit Institute*/}
          <div className="form-group">
            <label>Has Subscribed</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="hasSubscribed"
                checked={editFormData.hasSubscribed}
                onChange={handleEditInputChange}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {editFormData.hasSubscribed ? "Yes" : "No"}
              </span>
            </label>
          </div>
          {/* hasSubscribed is Allowed Closing  */}

          {/* isCreditReportEnabled Toggle Switch  Edit Institute*/}
          <div className="form-group">
            <label>Enable Credit Report</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="isCreditReportEnabled"
                checked={editFormData.isCreditReportEnabled}
                onChange={handleEditInputChange}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {editFormData.isCreditReportEnabled ? "Yes" : "No"}
              </span>
            </label>
          </div>

          {/* Allow Student Class Join Toggle Switch */}
          <div className="form-group">
            <label>Allow Students to Join Class</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="allowStudentClassJoin"
                checked={editFormData.allowStudentClassJoin}
                onChange={handleEditInputChange}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {editFormData.allowStudentClassJoin ? "Yes" : "No"}
              </span>
            </label>
          </div>

          {/* Auto Generate Student Password Toggle Switch */}
          <div className="form-group">
            <label>Auto Generate Student Password</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="autoGenerateStudentPassword"
                checked={editFormData.autoGenerateStudentPassword}
                onChange={handleEditInputChange}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {editFormData.autoGenerateStudentPassword ? "Yes" : "No"}
              </span>
            </label>
          </div>

          {/* Webhook Enabled Toggle Switch */}
          <div className="form-group">
            <label>Webhook Enabled</label>
            <label className="solution-toggle global-toggle">
              <input
                type="checkbox"
                name="webhookEnabled"
                checked={editFormData.webhookEnabled}
                onChange={handleEditInputChange}
              />
              <span className="toggle-track">
                <span className="toggle-indicator"></span>
              </span>
              <span className="toggle-label-text">
                {editFormData.webhookEnabled ? "Yes" : "No"}
              </span>
            </label>
          </div>

          {editFormData.webhookEnabled && (
            <div className="form-group">
              <label htmlFor="webhookURL">Webhook URL</label>
              <input
                type="url"
                id="webhookURL"
                name="webhookURL"
                value={editFormData.webhookURL}
                onChange={handleEditInputChange}
                placeholder="Enter webhook URL"
              />
            </div>
          )}
          <div className="form-group">
            <label htmlFor="websiteURL">Data Region</label>
            <Select
              id="edit-dataRegion"
              name="dataRegion"
              options={dataRegionOptions}
              value={dataRegionOptions.find(
                (option) => option.value === editFormData.dataRegion,
              )}
              onChange={handleEditSelectChange}
              placeholder="Select region..."
              isClearable
            />
          </div>

          {formError && <div className="error-message">{formError}</div>}
          <button
            type="submit"
            className="btn-submit"
            disabled={!isEditChanged || isSubmitting}
          >
            {isSubmitting ? (
              <Loader
                style={{ fontSize: "1em", color: "white", top: "0", left: "0" }}
              />
            ) : (
              <>
                <i className="fa-solid fa-floppy-disk"></i> Save
              </>
            )}
          </button>
        </form>
      </Modal>

      {/* Manage Credit Modal */}
      <Modal
        isOpen={isCreditModalOpen}
        onClose={() => !isCreditSubmitting && setIsCreditModalOpen(false)}
        maxWidth="650px"
      >
        <h2>Manage Credits</h2>
        {selectedCreditInstitute && (
          <div
            style={{
              backgroundColor: "#f4f6f8",
              padding: "12px 16px",
              borderRadius: "8px",
              marginBottom: "20px",
              display: "flex",
              flexWrap: "wrap",
              gap: "20px",
              fontSize: "14px",
              color: "#333",
              border: "1px solid #e0e0e0",
            }}
          >
            <div>
              <span style={{ color: "#666" }}>Institute Code: </span>
              <strong>{selectedCreditInstitute.code}</strong>
            </div>
            <div>
              <span style={{ color: "#666" }}>Institute Name: </span>
              <strong>{selectedCreditInstitute.name}</strong>
            </div>
          </div>
        )}

        <form onSubmit={handleSaveCredit}>
          {/* Mode Selection */}
          <div
            style={{
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "10px 14px",
              backgroundColor: "#f8fafc",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
            }}
          >
            <span style={{ fontWeight: "600", fontSize: "14px", color: "#334155" }}>
              Calculation Mode:
            </span>
            <button
              type="button"
              onClick={() => handleModeChange("auto")}
              style={{
                padding: "6px 16px",
                borderRadius: "20px",
                cursor: "pointer",
                fontWeight: "600",
                fontSize: "13px",
                backgroundColor: creditMode === "auto" ? "#2563eb" : "#ffffff",
                color: creditMode === "auto" ? "#ffffff" : "#475569",
                border: creditMode === "auto" ? "1px solid #2563eb" : "1px solid #cbd5e1",
                boxShadow: creditMode === "auto" ? "0 2px 4px rgba(37,99,235,0.2)" : "none",
                transition: "all 0.2s ease",
              }}
            >
              <i className="fa-solid fa-calculator" style={{ marginRight: "6px" }}></i>
              Auto Compute (Default)
            </button>
            <button
              type="button"
              onClick={() => handleModeChange("manual")}
              style={{
                padding: "6px 16px",
                borderRadius: "20px",
                cursor: "pointer",
                fontWeight: "600",
                fontSize: "13px",
                backgroundColor: creditMode === "manual" ? "#2563eb" : "#ffffff",
                color: creditMode === "manual" ? "#ffffff" : "#475569",
                border: creditMode === "manual" ? "1px solid #2563eb" : "1px solid #cbd5e1",
                boxShadow: creditMode === "manual" ? "0 2px 4px rgba(37,99,235,0.2)" : "none",
                transition: "all 0.2s ease",
              }}
            >
              <i className="fa-solid fa-pen" style={{ marginRight: "6px" }}></i>
              Manual Edit
            </button>
          </div>

          <div style={{ marginBottom: "24px" }}>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                marginBottom: "14px",
                padding: "8px 14px",
                backgroundColor: "#fef08a",
                color: "#854d0e",
                borderRadius: "6px",
                borderLeft: "4px solid #eab308",
              }}
            >
              Grading Credits
            </h3>

            {creditMode === "auto" ? (
              <>
                <div
                  style={{
                    display: "flex",
                    gap: "20px",
                    padding: "10px 14px",
                    backgroundColor: "#eff6ff",
                    borderRadius: "6px",
                    marginBottom: "12px",
                    fontSize: "13px",
                    border: "1px solid #bfdbfe",
                  }}
                >
                  <div>
                    <span style={{ color: "#64748b" }}>Total Available: </span>
                    <strong>{usageMap[selectedCreditInstitute?._id]?.combinedGrading?.purchasedSize ?? 0}</strong>
                  </div>
                  <div>
                    <span style={{ color: "#64748b" }}>Credits Used: </span>
                    <strong>{usageMap[selectedCreditInstitute?._id]?.combinedGrading?.usageSize ?? 0}</strong>
                  </div>
                  <div>
                    <span style={{ color: "#64748b" }}>Remaining: </span>
                    <strong>
                      {Math.max(
                        0,
                        (usageMap[selectedCreditInstitute?._id]?.combinedGrading?.purchasedSize ?? 0) -
                          (usageMap[selectedCreditInstitute?._id]?.combinedGrading?.usageSize ?? 0),
                      )}
                    </strong>
                  </div>
                </div>

                <div style={{ fontSize: "13px", color: "#475569", marginBottom: "12px" }}>
                  Last Purchase:{" "}
                  {creditFormData.gradingLastPurchasedCredits ? (
                    <strong>
                      {creditFormData.gradingLastPurchasedCredits} credits on{" "}
                      {creditFormData.gradingLastPurchasedDate
                        ? displayDateOnly(creditFormData.gradingLastPurchasedDate)
                        : "—"}
                    </strong>
                  ) : (
                    "No purchase history"
                  )}
                </div>

                <div
                  style={{
                    padding: "12px 14px",
                    backgroundColor: "#f0fdf4",
                    borderRadius: "8px",
                    border: "1px solid #bbf7d0",
                    marginBottom: "12px",
                  }}
                >
                  <div style={{ fontWeight: "600", fontSize: "13px", color: "#166534", marginBottom: "10px" }}>
                    Renew Credits
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                    <div className="form-group">
                      <label htmlFor="credit-gradingRenewCredits">Credits to Add</label>
                      <input
                        type="number"
                        id="credit-gradingRenewCredits"
                        name="gradingRenewCredits"
                        value={creditFormData.gradingRenewCredits}
                        onChange={handleCreditInputChange}
                        min="0"
                        step="1"
                        placeholder="e.g. 300"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="credit-gradingRenewDate">Purchase Date</label>
                      <input
                        type="date"
                        id="credit-gradingRenewDate"
                        name="gradingRenewDate"
                        value={creditFormData.gradingRenewDate}
                        onChange={handleCreditInputChange}
                      />
                    </div>
                  </div>
                </div>

                {creditFormData.gradingRenewCredits !== "" &&
                creditFormData.gradingRenewDate &&
                (() => {
                  const instUsage = usageMap[selectedCreditInstitute?._id];
                  const used = instUsage?.combinedGrading?.usageSize ?? 0;
                  const total = instUsage?.combinedGrading?.purchasedSize ?? 0;
                  const remaining = Math.max(0, total - used);

                  const expDateStr = creditFormData.gradingCreditExpiryDate;
                  let isExpired = false;

                  if (expDateStr) {
                    const expDate = new Date(expDateStr + "T00:00:00.000Z");
                    isExpired = !isNaN(expDate.getTime()) && expDate < new Date();
                  }

                  const renewCredits =
                    parseInt(creditFormData.gradingRenewCredits) || 0;

                  const finalAvailable = isExpired
                    ? renewCredits
                    : remaining + renewCredits;

                  return (
                    <div
                      style={{
                        padding: "12px 14px",
                        backgroundColor: "#fffbeb",
                        borderRadius: "8px",
                        border: "1px solid #fde68a",
                        marginBottom: "12px",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "600",
                          fontSize: "13px",
                          color: "#92400e",
                          marginBottom: "10px",
                        }}
                      >
                        Preview (to be saved)
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: "12px",
                        }}
                      >
                        <div className="form-group">
                          <label>Available Credits</label>
                          <input
                            type="number"
                            value={finalAvailable}
                            disabled
                          />
                        </div>

                        <div className="form-group">
                          <label>Past Purchased Credits</label>
                          <input
                            type="number"
                            value={creditFormData.gradingPastPurchased}
                            disabled
                          />
                        </div>

                        <div className="form-group">
                          <label>Purchased Credit</label>
                          <input
                            type="number"
                            value={renewCredits}
                            disabled
                          />
                        </div>

                        <div className="form-group">
                          <label>Purchased Date</label>
                          <input
                            type="date"
                            value={creditFormData.gradingRenewDate}
                            disabled
                          />
                        </div>

                        <div
                          className="form-group"
                          style={{ gridColumn: "1 / -1" }}
                        >
                          <label>Expiry Date</label>
                          <input
                            type="date"
                            value={creditFormData.gradingCreditExpiryDate}
                            disabled
                          />
                          <p
                            style={{
                              fontSize: "12px",
                              color: "#92400e",
                              marginTop: "4px",
                              marginBottom: "0",
                            }}
                          >
                            * Note: Save the credits first, then update expiry date via manual edit.
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div className="form-group">
                  <label htmlFor="credit-gradingAvailable">Available Credits</label>
                  <input
                    type="number"
                    id="credit-gradingAvailable"
                    name="gradingAvailable"
                    value={creditFormData.gradingAvailable}
                    onChange={handleCreditInputChange}
                    min="0"
                    step="1"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="credit-gradingPastPurchased">Past Purchased Credits</label>
                  <input
                    type="number"
                    id="credit-gradingPastPurchased"
                    name="gradingPastPurchased"
                    value={creditFormData.gradingPastPurchased}
                    onChange={handleCreditInputChange}
                    min="0"
                    step="1"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="credit-gradingLastPurchasedCredits">Purchased Credit</label>
                  <input
                    type="number"
                    id="credit-gradingLastPurchasedCredits"
                    name="gradingLastPurchasedCredits"
                    value={creditFormData.gradingLastPurchasedCredits}
                    onChange={handleCreditInputChange}
                    min="0"
                    step="1"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="credit-gradingLastPurchasedDate">Purchased Date</label>
                  <input
                    type="date"
                    id="credit-gradingLastPurchasedDate"
                    name="gradingLastPurchasedDate"
                    value={creditFormData.gradingLastPurchasedDate}
                    onChange={handleCreditInputChange}
                  />
                </div>
                <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                  <label htmlFor="credit-gradingCreditExpiryDate">Expiry Date</label>
                  <input
                    type="date"
                    id="credit-gradingCreditExpiryDate"
                    name="gradingCreditExpiryDate"
                    value={creditFormData.gradingCreditExpiryDate}
                    onChange={handleCreditInputChange}
                  />
                </div>
              </div>
            )}
          </div>

          <div style={{ marginBottom: "24px" }}>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                marginBottom: "14px",
                padding: "8px 14px",
                backgroundColor: "#fef08a",
                color: "#854d0e",
                borderRadius: "6px",
                borderLeft: "4px solid #eab308",
              }}
            >
              Question Bank Credits
            </h3>

            {creditMode === "auto" ? (
              <>
                <div
                  style={{
                    display: "flex",
                    gap: "20px",
                    padding: "10px 14px",
                    backgroundColor: "#eff6ff",
                    borderRadius: "6px",
                    marginBottom: "12px",
                    fontSize: "13px",
                    border: "1px solid #bfdbfe",
                  }}
                >
                  <div>
                    <span style={{ color: "#64748b" }}>Total Available: </span>
                    <strong>
                      {usageMap[selectedCreditInstitute?._id]?.questionBlock?.purchasedSize ?? 0}
                    </strong>
                  </div>

                  <div>
                    <span style={{ color: "#64748b" }}>Credits Used: </span>
                    <strong>
                      {usageMap[selectedCreditInstitute?._id]?.questionBlock?.usageSize ?? 0}
                    </strong>
                  </div>

                  <div>
                    <span style={{ color: "#64748b" }}>Remaining: </span>
                    <strong>
                      {Math.max(
                        0,
                        (usageMap[selectedCreditInstitute?._id]?.questionBlock?.purchasedSize ?? 0) -
                          (usageMap[selectedCreditInstitute?._id]?.questionBlock?.usageSize ?? 0),
                      )}
                    </strong>
                  </div>
                </div>

                <div
                  style={{
                    fontSize: "13px",
                    color: "#475569",
                    marginBottom: "12px",
                  }}
                >
                  Last Purchase:{" "}
                  {creditFormData.qbLastPurchasedCredits ? (
                    <strong>
                      {creditFormData.qbLastPurchasedCredits} credits on{" "}
                      {creditFormData.qbLastPurchasedDate
                        ? displayDateOnly(creditFormData.qbLastPurchasedDate)
                        : "—"}
                    </strong>
                  ) : (
                    "No purchase history"
                  )}
                </div>

                <div
                  style={{
                    padding: "12px 14px",
                    backgroundColor: "#f0fdf4",
                    borderRadius: "8px",
                    border: "1px solid #bbf7d0",
                    marginBottom: "12px",
                  }}
                >
                  <div
                    style={{
                      fontWeight: "600",
                      fontSize: "13px",
                      color: "#166534",
                      marginBottom: "10px",
                    }}
                  >
                    Renew Credits
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "12px",
                    }}
                  >
                    <div className="form-group">
                      <label htmlFor="credit-qbRenewCredits">Credits to Add</label>
                      <input
                        type="number"
                        id="credit-qbRenewCredits"
                        name="qbRenewCredits"
                        value={creditFormData.qbRenewCredits}
                        onChange={handleCreditInputChange}
                        min="0"
                        step="1"
                        placeholder="e.g. 300"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="credit-qbRenewDate">Purchase Date</label>
                      <input
                        type="date"
                        id="credit-qbRenewDate"
                        name="qbRenewDate"
                        value={creditFormData.qbRenewDate}
                        onChange={handleCreditInputChange}
                      />
                    </div>
                  </div>
                </div>

                {creditFormData.qbRenewCredits !== "" &&
                  creditFormData.qbRenewDate &&
                  (() => {
                    const instUsage = usageMap[selectedCreditInstitute?._id];
                    const used = instUsage?.questionBlock?.usageSize ?? 0;
                    const total = instUsage?.questionBlock?.purchasedSize ?? 0;
                    const remaining = Math.max(0, total - used);
                    const renewCredits =
                      parseInt(creditFormData.qbRenewCredits) || 0;

                    const finalAvailable = remaining + renewCredits;

                    return (
                      <div
                        style={{
                          padding: "12px 14px",
                          backgroundColor: "#fffbeb",
                          borderRadius: "8px",
                          border: "1px solid #fde68a",
                          marginBottom: "12px",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: "600",
                            fontSize: "13px",
                            color: "#92400e",
                            marginBottom: "10px",
                          }}
                        >
                          Preview (to be saved)
                        </div>

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "12px",
                          }}
                        >
                          <div className="form-group">
                            <label htmlFor="credit-qbPreviewAvailable">
                              Available Credits
                            </label>
                            <input
                              type="number"
                              id="credit-qbPreviewAvailable"
                              value={finalAvailable}
                              disabled
                            />
                          </div>

                          <div className="form-group">
                            <label htmlFor="credit-qbPreviewPastPurchased">
                              Past Purchased Credits
                            </label>
                            <input
                              type="number"
                              id="credit-qbPreviewPastPurchased"
                              value={creditFormData.qbPastPurchased}
                              disabled
                            />
                          </div>

                          <div className="form-group">
                            <label htmlFor="credit-qbPreviewPurchasedCredit">
                              Purchased Credit
                            </label>
                            <input
                              type="number"
                              id="credit-qbPreviewPurchasedCredit"
                              value={renewCredits}
                              disabled
                            />
                          </div>

                          <div className="form-group">
                            <label htmlFor="credit-qbPreviewPurchasedDate">
                              Purchased Date
                            </label>
                            <input
                              type="date"
                              id="credit-qbPreviewPurchasedDate"
                              value={creditFormData.qbRenewDate}
                              disabled
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })()}
              </>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px",
                }}
              >
                <div className="form-group">
                  <label htmlFor="credit-qbAvailable">Available Credits</label>
                  <input
                    type="number"
                    id="credit-qbAvailable"
                    name="qbAvailable"
                    value={creditFormData.qbAvailable}
                    onChange={handleCreditInputChange}
                    min="0"
                    step="1"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="credit-qbPastPurchased">
                    Past Purchased Credits
                  </label>
                  <input
                    type="number"
                    id="credit-qbPastPurchased"
                    name="qbPastPurchased"
                    value={creditFormData.qbPastPurchased}
                    onChange={handleCreditInputChange}
                    min="0"
                    step="1"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="credit-qbLastPurchasedCredits">
                    Purchased Credit
                  </label>
                  <input
                    type="number"
                    id="credit-qbLastPurchasedCredits"
                    name="qbLastPurchasedCredits"
                    value={creditFormData.qbLastPurchasedCredits}
                    onChange={handleCreditInputChange}
                    min="0"
                    step="1"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="credit-qbLastPurchasedDate">
                    Purchased Date
                  </label>
                  <input
                    type="date"
                    id="credit-qbLastPurchasedDate"
                    name="qbLastPurchasedDate"
                    value={creditFormData.qbLastPurchasedDate}
                    onChange={handleCreditInputChange}
                  />
                </div>
              </div>
            )}
          </div>

          {creditFormError && <div className="error-message">{creditFormError}</div>}

          <button
            type="submit"
            className="btn-submit"
            disabled={isCreditSubmitting}
          >
            {isCreditSubmitting ? (
              <Loader
                style={{ fontSize: "1em", color: "white", top: "0", left: "0" }}
              />
            ) : (
              <>
                <i className="fa-solid fa-floppy-disk"></i> Save Credits
              </>
            )}
          </button>
        </form>
      </Modal>

      {/* Bulk Delete Institutes Confirmation Modal */}
      <Modal
        isOpen={isBulkDeleteModalOpen}
        onClose={() => !isBulkDeleting && setIsBulkDeleteModalOpen(false)}
        closeButton={!isBulkDeleting}
        maxWidth="620px"
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", marginBottom: "16px" }}>
          <div
            style={{
              width: "44px",
              height: "44px",
              borderRadius: "50%",
              background: "#fee2e2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#dc2626",
              fontSize: "1.25rem",
              flexShrink: 0,
            }}
          >
            <i className="fa-solid fa-triangle-exclamation"></i>
          </div>
          <div>
            <h2 style={{ margin: "0 0 4px 0", fontSize: "1.3rem", color: "#111827", fontWeight: 700 }}>
              Delete {selectedInstitutesList.length} Institute{selectedInstitutesList.length === 1 ? "" : "s"}
            </h2>
            <p style={{ margin: 0, fontSize: "0.85rem", color: "#6b7280" }}>
              Are you sure you want to delete these institutes? This action is permanent and cannot be undone.
            </p>
          </div>
        </div>

        <div
          style={{
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: "8px",
            padding: "10px 14px",
            marginBottom: "16px",
            fontSize: "0.84rem",
            color: "#991b1b",
            lineHeight: "1.4",
          }}
        >
          <strong>Warning:</strong> Deleting these institutes will permanently erase all associated data including classes, teachers, student accounts, exams, and token allocations.
        </div>

        <div style={{ marginBottom: "16px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "6px",
              fontSize: "0.85rem",
              fontWeight: 600,
              color: "#374151",
            }}
          >
            <span>Institutes selected for deletion:</span>
            <span style={{ color: "#6b7280", fontWeight: 500 }}>
              {selectedInstitutesList.length} total
            </span>
          </div>
          <div
            style={{
              maxHeight: "160px",
              overflowY: "auto",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              background: "#f9fafb",
              padding: "6px 8px",
              display: "flex",
              flexDirection: "column",
              gap: "6px",
            }}
          >
            {selectedInstitutesList.map((inst, idx) => {
              const status = bulkDeleteProgress.statusMap[inst._id];
              return (
                <div
                  key={inst._id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "6px 10px",
                    background: "#ffffff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "0.85rem",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      overflow: "hidden",
                      flexWrap: "wrap",
                    }}
                  >
                    <span style={{ fontWeight: 600, color: "#6b7280", fontSize: "0.78rem" }}>
                      #{idx + 1}
                    </span>
                    <span
                      style={{
                        fontWeight: 600,
                        color: "#1f2937",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        maxWidth: "180px",
                      }}
                      title={inst.name}
                    >
                      {inst.name}
                    </span>
                    <span
                      style={{
                        fontSize: "0.75rem",
                        color: "#4b5563",
                        background: "#f3f4f6",
                        padding: "1px 6px",
                        borderRadius: "4px",
                      }}
                      title="Institute Code"
                    >
                      {inst.code}
                    </span>
                    {inst.hasSubscribed && (
                      <span
                        style={{
                          fontSize: "0.7rem",
                          color: "#92400e",
                          background: "#fef3c7",
                          padding: "1px 6px",
                          borderRadius: "4px",
                          fontWeight: 600,
                        }}
                      >
                        Subscribed
                      </span>
                    )}
                    {/* Credit Expiry Date - shown after subscribed (or after code if non-subscribed) */}
                    <span
                      style={{
                        fontSize: "0.72rem",
                        color: isCreditExpired(inst) ? "#b91c1c" : "#4b5563",
                        background: isCreditExpired(inst) ? "#fee2e2" : "#f3f4f6",
                        border: isCreditExpired(inst)
                          ? "1px solid #fecaca"
                          : "1px solid #e5e7eb",
                        padding: "1px 6px",
                        borderRadius: "4px",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                      title={`Credit Expiry Date: ${
                        inst.gradingCreditExpiryDate
                          ? displayDateOnly(inst.gradingCreditExpiryDate)
                          : "Not Set"
                      }`}
                    >
                      <i
                        className={
                          isCreditExpired(inst)
                            ? "fa-solid fa-calendar-xmark"
                            : "fa-solid fa-calendar-days"
                        }
                        style={{
                          fontSize: "0.68rem",
                          color: isCreditExpired(inst) ? "#dc2626" : "#6b7280",
                        }}
                      ></i>
                      <span>
                        {inst.gradingCreditExpiryDate
                          ? displayDateOnly(inst.gradingCreditExpiryDate)
                          : "No Expiry"}
                      </span>
                    </span>
                  </div>
                  <div>
                    {status === "deleting" && (
                      <span
                        style={{
                          fontSize: "0.8rem",
                          color: "#2563eb",
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <i className="fa-solid fa-spinner fa-spin"></i> Deleting
                      </span>
                    )}
                    {status === "success" && (
                      <span
                        style={{
                          fontSize: "0.8rem",
                          color: "#16a34a",
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <i className="fa-solid fa-check"></i> Deleted
                      </span>
                    )}
                    {status === "error" && (
                      <span
                        style={{
                          fontSize: "0.8rem",
                          color: "#dc2626",
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                        title={bulkDeleteProgress.errorMap[inst._id] || "Failed"}
                      >
                        <i className="fa-solid fa-xmark"></i> Failed
                      </span>
                    )}
                    {(!status || status === "pending") && isBulkDeleting && (
                      <span style={{ fontSize: "0.75rem", color: "#9ca3af" }}>
                        Queued
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {isBulkDeleting && (
          <div style={{ marginBottom: "18px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "0.85rem",
                marginBottom: "6px",
              }}
            >
              <span style={{ color: "#374151", fontWeight: 600 }}>
                Deleting institutes: {bulkDeleteProgress.current} of {bulkDeleteProgress.total}
              </span>
              <span style={{ color: "#6b7280", fontWeight: 600 }}>
                {Math.round((bulkDeleteProgress.current / (bulkDeleteProgress.total || 1)) * 100)}%
              </span>
            </div>
            <div
              style={{
                width: "100%",
                height: "8px",
                background: "#e5e7eb",
                borderRadius: "999px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${Math.round((bulkDeleteProgress.current / (bulkDeleteProgress.total || 1)) * 100)}%`,
                  height: "100%",
                  background: "linear-gradient(90deg, #ef4444, #dc2626)",
                  transition: "width 0.25s ease",
                }}
              />
            </div>
            {bulkDeleteProgress.currentName && bulkDeleteProgress.current < bulkDeleteProgress.total && (
              <div style={{ marginTop: "6px", fontSize: "0.8rem", color: "#6b7280" }}>
                Currently deleting: <em>"{bulkDeleteProgress.currentName}"</em>...
              </div>
            )}
          </div>
        )}

        {!isBulkDeleting && (
          <div style={{ marginBottom: "20px" }}>
            <label
              htmlFor="bulk-delete-confirm-input"
              style={{
                display: "block",
                marginBottom: "6px",
                fontSize: "0.875rem",
                fontWeight: 600,
                color: "#374151",
              }}
            >
              To confirm deletion, please type <span style={{ color: "#dc2626", fontWeight: 700 }}>DELETE</span> below:
            </label>
            <input
              id="bulk-delete-confirm-input"
              type="text"
              value={bulkDeleteConfirmationText}
              onChange={(e) => setBulkDeleteConfirmationText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (bulkDeleteConfirmationText.trim().toUpperCase() === "DELETE") {
                    handleExecuteBulkDelete();
                  }
                }
              }}
              placeholder='Type "DELETE" to confirm'
              autoFocus
              style={{
                width: "100%",
                padding: "9px 12px",
                border:
                  bulkDeleteConfirmationText.trim().toUpperCase() === "DELETE"
                    ? "2px solid #16a34a"
                    : "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "0.95rem",
                outline: "none",
                boxSizing: "border-box",
                transition: "border-color 0.2s ease",
              }}
            />
            {bulkDeleteConfirmationText.length > 0 &&
              bulkDeleteConfirmationText.trim().toUpperCase() !== "DELETE" && (
                <p style={{ margin: "4px 0 0", fontSize: "0.78rem", color: "#dc2626" }}>
                  Please enter "DELETE" to enable the delete button
                </p>
              )}
            {bulkDeleteConfirmationText.trim().toUpperCase() === "DELETE" && (
              <p
                style={{
                  margin: "4px 0 0",
                  fontSize: "0.78rem",
                  color: "#16a34a",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <i className="fa-solid fa-circle-check"></i> Confirmation verified. Ready to delete.
              </p>
            )}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          <button
            type="button"
            onClick={() => {
              setIsBulkDeleteModalOpen(false);
              setBulkDeleteConfirmationText("");
            }}
            disabled={isBulkDeleting}
            style={{
              padding: "8px 16px",
              borderRadius: "6px",
              border: "1px solid #d1d5db",
              background: "#ffffff",
              color: "#374151",
              fontSize: "0.9rem",
              fontWeight: 500,
              cursor: isBulkDeleting ? "not-allowed" : "pointer",
              opacity: isBulkDeleting ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleExecuteBulkDelete}
            disabled={bulkDeleteConfirmationText.trim().toUpperCase() !== "DELETE" || isBulkDeleting}
            style={{
              padding: "8px 20px",
              borderRadius: "6px",
              border: "none",
              background:
                bulkDeleteConfirmationText.trim().toUpperCase() === "DELETE" && !isBulkDeleting
                  ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                  : "#fca5a5",
              color: "#ffffff",
              fontSize: "0.9rem",
              fontWeight: 600,
              cursor:
                bulkDeleteConfirmationText.trim().toUpperCase() === "DELETE" && !isBulkDeleting
                  ? "pointer"
                  : "not-allowed",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              boxShadow:
                bulkDeleteConfirmationText.trim().toUpperCase() === "DELETE" && !isBulkDeleting
                  ? "0 2px 8px rgba(220, 38, 38, 0.3)"
                  : "none",
            }}
          >
            {isBulkDeleting ? (
              <>
                <i className="fa-solid fa-spinner fa-spin"></i>
                <span>Deleting Institutes...</span>
              </>
            ) : (
              <>
                <i className="fa-solid fa-trash"></i>
                <span>
                  Delete {selectedInstitutesList.length} Institute{selectedInstitutesList.length === 1 ? "" : "s"}
                </span>
              </>
            )}
          </button>
        </div>
      </Modal>
    </div>
  );
}
